import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { repairRequestService } from '../services/repairRequestService';
import { serviceProviderService } from '../services/serviceProviderService';
import { ServiceWorkspaceShell } from '../components/Service/ServiceWorkspaceShell';
import { PageEmptyState, PageErrorState, PageLoadingState } from '../components/common/ViewStates';
import { Alert, Badge, Button, Input } from '../components/common/UIComponents';
import { BusinessHours, BusinessHoursDayKey, RepairRequest } from '../types/firestore';
import {
  getRepairRequestId,
  isRepairRequestAssignedToServiceUser,
} from '../utils/repairRequestReadModel';
import {
  addDaysToServicePlannerDayKey,
  dateFromServicePlannerDayKey,
  deriveServicePlannerItems,
  getServicePlannerDayKey,
  getServicePlannerWeekDayKeys,
  type ServicePlannerDayTimeGrid,
  type ServicePlannerDayWorkloadSummary,
  type ServicePlannerItem,
  type ServicePlannerTimeGridBlock,
  type ServicePlannerWeekDay,
} from '../utils/servicePlannerReadModel';
import {
  getRepairRequestOperationalIndexLabel,
  getRepairRequestSubjectLabel,
} from '../shared/repairRequestDisplay';
import {
  getRepairRequestStatusLabel,
  isTerminalRepairRequestStatus,
} from '../utils/repairRequestWorkflow';
import { normalizeBusinessHours } from '../utils/businessHours';

const plannerDateTimeFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const plannerDateFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  day: '2-digit',
  month: 'short',
});

const plannerLongDateFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const plannerDayMonthFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  day: 'numeric',
  month: 'long',
});

const plannerDayOnlyFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  day: 'numeric',
});

const plannerMonthYearFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  month: 'long',
  year: 'numeric',
});

const plannerWeekdayShortFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  weekday: 'short',
});

const plannerTimeFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  hour: '2-digit',
  minute: '2-digit',
});

const plannerInputDateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Warsaw',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const SERVICE_PLANNER_TIMEZONE = 'Europe/Warsaw';
const SERVICE_SCHEDULE_NOTE_MAX_LENGTH = 500;
const PLANNER_DESKTOP_LAYOUT_MIN_WIDTH_PX = 1024;
const PLANNER_DECISION_SCROLL_MARGIN_CLASS = 'scroll-mt-[4.75rem] sm:scroll-mt-[5.25rem]';
const SERVICE_PLANNER_HOUR_ROW_HEIGHT_PX = 72;
const TIME_GRID_STEP_MINUTES = 30;
const SERVICE_PLANNER_DEFAULT_WORKING_START = '08:00';
const SERVICE_PLANNER_DEFAULT_WORKING_END = '18:00';
const PLANNER_ZONE_SURFACE_CLASS = 'rs-planner-shell';
const PLANNER_PANEL_SURFACE_CLASS = 'rs-planner-side-zone';
const PLANNER_PANEL_QUIET_SURFACE_CLASS = 'rs-planner-side-zone rs-planner-side-zone--muted';
const PLANNER_ITEM_BASE_CLASS = 'rs-planner-event-card';
const PLANNER_ITEM_SURFACE_CLASS = 'rs-planner-event-card--idle';
const PLANNER_ITEM_ACTIVE_SURFACE_CLASS = 'rs-planner-event-card--active';
const PLANNER_ITEM_DRAG_SURFACE_CLASS = 'rs-planner-event-card--dragging';
const PLANNER_ACTION_ROW_CLASS = 'mt-3 flex min-w-0 flex-wrap items-center gap-2';
const PLANNER_DECISION_SURFACE_CLASS = 'rs-planner-decision-surface';
const PLANNER_GRID_SURFACE_CLASS = 'rs-planner-canvas';
const PLANNER_GRID_EMPTY_COPY_CLASS = 'rs-planner-grid-empty-copy';
const PLANNER_TONAL_BUTTON_CLASS =
  'border-transparent bg-primary-dark-200/46 text-primary-light-100 shadow-none hover:bg-primary-dark-300/52';
const PLANNER_NAV_LINK_CLASS =
  'inline-flex min-h-[34px] shrink-0 items-center justify-center gap-1.5 rounded-lg border border-transparent bg-primary-dark-200/38 px-3 py-1.5 text-xs font-semibold text-primary-light-100 transition-colors hover:bg-primary-dark-300/48 focus-outline';
const PLANNER_DESTRUCTIVE_TONAL_BUTTON_CLASS =
  'border-transparent bg-status-error-500/10 text-status-error-300 shadow-none hover:bg-status-error-500/16';

type PlannerActionMode = 'schedule' | 'reschedule';
type PlannerViewMode = 'day' | 'week' | 'month';
type PlannerViewSegmentVariant = 'desktop' | 'mobile';
type PlannerItemDateMode = 'schedule' | 'scheduleTime' | 'preference';
type PlannerActionSource = 'manual' | 'calendarDrop' | 'timeSlot';
type PlannerDecisionViewport = 'desktop' | 'mobile';

interface PlannerDecisionAnchor {
  kind: 'form' | 'clear';
  requestId: string;
  nonce: number;
}

interface ActivePlannerSlot {
  dayKey: string;
  startTime: string;
}

interface ActivePlannerAction {
  requestId: string;
  mode: PlannerActionMode;
  source: PlannerActionSource;
  prefilledDayKey?: string;
  prefilledStartTime?: string;
}

interface PlannerFormValues {
  date: string;
  startTime: string;
  endTime: string;
  note: string;
}

interface PlannerFormErrors {
  date?: string;
  startTime?: string;
  endTime?: string;
  note?: string;
}

interface PlannerFormSchedulePreview {
  hasScheduleConflict: boolean;
  isHeavyDay: boolean;
  isOutsideWorkingHours: boolean;
  workingHoursLabel: string;
  workingHoursSource: 'businessHours' | 'default';
}

interface PlannerWorkingHoursForDay {
  source: 'businessHours' | 'default';
  isOpenDay: boolean;
  startTime: string | null;
  endTime: string | null;
  startMinutes: number | null;
  endMinutes: number | null;
  label: string;
}

interface PlannerDragSourceConfig {
  isEnabled: boolean;
  draggingRequestId: string | null;
  onDragStart: (event: React.DragEvent<HTMLElement>, item: ServicePlannerItem) => void;
  onDragEnd: () => void;
}

interface PlannerDropTargetConfig {
  isEnabled: boolean;
  isDayEnabled: boolean;
  isSlotEnabled: boolean;
  activeDayKey: string | null;
  activeSlotKey: string | null;
  onDragOver: (event: React.DragEvent<HTMLElement>, dayKey: string, startTime?: string) => void;
  onDragLeave: (event: React.DragEvent<HTMLElement>, dayKey: string, startTime?: string) => void;
  onDrop: (event: React.DragEvent<HTMLElement>, dayKey: string, startTime?: string) => void;
}

interface ClearPlannerState {
  requestId: string;
  isSubmitting: boolean;
  error: string | null;
}

type PlannerDeepLinkState =
  | {
      kind: 'ready';
      requestId: string;
      item: ServicePlannerItem;
      mode: PlannerActionMode;
    }
  | {
      kind: 'terminal';
      requestId: string;
    }
  | {
      kind: 'missing';
      requestId: string;
    }
  | null;

const EMPTY_PLANNER_FORM_VALUES: PlannerFormValues = {
  date: '',
  startTime: '',
  endTime: '',
  note: '',
};
const EMPTY_PLANNER_FORM_SCHEDULE_PREVIEW: PlannerFormSchedulePreview = {
  hasScheduleConflict: false,
  isHeavyDay: false,
  isOutsideWorkingHours: false,
  workingHoursLabel: `Standardowe okno planera: ${SERVICE_PLANNER_DEFAULT_WORKING_START}-${SERVICE_PLANNER_DEFAULT_WORKING_END}`,
  workingHoursSource: 'default',
};
const PLANNER_DRAG_MIN_WIDTH_PX = 768;
const SERVICE_PLANNER_DRAG_MIME = 'application/x-ratuj-service-planner-request';

const formatPlannerDateTime = (date: Date | null): string | null =>
  date ? plannerDateTimeFormatter.format(date) : null;

const formatPlannerDate = (date: Date | null): string | null =>
  date ? plannerDateFormatter.format(date) : null;

const formatPlannerTime = (date: Date | null): string | null =>
  date ? plannerTimeFormatter.format(date) : null;

const formatPlannerWorkloadCount = (count: number): string => {
  const normalizedCount = Math.max(0, count);
  const mod10 = normalizedCount % 10;
  const mod100 = normalizedCount % 100;
  const noun =
    normalizedCount === 1
      ? 'zlecenie'
      : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
        ? 'zlecenia'
        : 'zleceń';

  return `${normalizedCount} ${noun}`;
};

const formatPlannerWorkloadRange = (summary: ServicePlannerDayWorkloadSummary): string | null => {
  const firstTime = formatPlannerTime(summary.firstScheduledAt);
  const lastTime = formatPlannerTime(summary.lastScheduledAt);

  if (!firstTime) {
    return null;
  }
  if (!lastTime || firstTime === lastTime) {
    return firstTime;
  }
  return `${firstTime}–${lastTime}`;
};

const formatPlannerWorkloadSummary = (summary: ServicePlannerDayWorkloadSummary): string => {
  const countLabel = formatPlannerWorkloadCount(summary.scheduledCount);
  const rangeLabel = formatPlannerWorkloadRange(summary);
  return rangeLabel ? `${countLabel} · ${rangeLabel}` : countLabel;
};

const formatPlannerTimeFromMinutes = (minutes: number): string => {
  const boundedMinutes = Math.max(0, Math.min(24 * 60 - 1, minutes));
  const hour = Math.floor(boundedMinutes / 60);
  const minute = boundedMinutes % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const formatPlannerHourLabel = (hour: number): string => `${String(hour).padStart(2, '0')}:00`;

const formatPlannerTimeSlotLabel = (minutes: number): string =>
  formatPlannerTimeFromMinutes(minutes);

const formatPlannerDuration = (minutes: number | null): string | null => {
  if (!minutes || minutes <= 0) {
    return null;
  }
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  if (hours > 0 && restMinutes > 0) {
    return `${hours} h ${restMinutes} min`;
  }
  if (hours > 0) {
    return `${hours} h`;
  }
  return `${restMinutes} min`;
};

const parsePlannerTimeValueToMinutes = (value: string | null | undefined): number | null => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(value || '').trim());
  if (!match) {
    return null;
  }
  const [, hourRaw, minuteRaw] = match;
  return Number(hourRaw) * 60 + Number(minuteRaw);
};

const getPlannerTimeGridSlots = (
  timeGrid: ServicePlannerDayTimeGrid
): Array<{ minutes: number; startTime: string }> => {
  const startMinutes = timeGrid.rangeStartHour * 60;
  const endMinutes = timeGrid.rangeEndHour * 60;
  const slots: Array<{ minutes: number; startTime: string }> = [];

  for (let minutes = startMinutes; minutes < endMinutes; minutes += TIME_GRID_STEP_MINUTES) {
    slots.push({
      minutes,
      startTime: formatPlannerTimeSlotLabel(minutes),
    });
  }

  return slots;
};

const getPlannerExtendedTimeGrid = (
  timeGrid: ServicePlannerDayTimeGrid,
  beforeHours: number,
  afterHours: number
): ServicePlannerDayTimeGrid => {
  const rangeStartHour = Math.max(0, timeGrid.rangeStartHour - beforeHours);
  const rangeEndHour = Math.min(24, timeGrid.rangeEndHour + afterHours);
  const rangeStartMinutes = rangeStartHour * 60;

  return {
    ...timeGrid,
    rangeStartHour,
    rangeEndHour,
    hours: Array.from(
      { length: Math.max(0, rangeEndHour - rangeStartHour) },
      (_, index) => rangeStartHour + index
    ),
    blocks: timeGrid.blocks.map((block) => ({
      ...block,
      topOffsetMinutes: Math.max(0, block.startMinutes - rangeStartMinutes),
    })),
  };
};

const getPlannerBusinessHoursDayKey = (dayKey: string): BusinessHoursDayKey | null => {
  const date = dateFromServicePlannerDayKey(dayKey);
  if (!Number.isFinite(date.getTime())) {
    return null;
  }
  const dayKeys: BusinessHoursDayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  return dayKeys[date.getUTCDay()] || null;
};

const getPlannerWorkingHoursForDay = (
  dayKey: string,
  businessHours: BusinessHours | null
): PlannerWorkingHoursForDay => {
  const defaultStartMinutes = parsePlannerTimeValueToMinutes(SERVICE_PLANNER_DEFAULT_WORKING_START);
  const defaultEndMinutes = parsePlannerTimeValueToMinutes(SERVICE_PLANNER_DEFAULT_WORKING_END);
  const defaultWorkingHours: PlannerWorkingHoursForDay = {
    source: 'default',
    isOpenDay: true,
    startTime: SERVICE_PLANNER_DEFAULT_WORKING_START,
    endTime: SERVICE_PLANNER_DEFAULT_WORKING_END,
    startMinutes: defaultStartMinutes,
    endMinutes: defaultEndMinutes,
    label: `Standardowe okno planera: ${SERVICE_PLANNER_DEFAULT_WORKING_START}-${SERVICE_PLANNER_DEFAULT_WORKING_END}`,
  };

  const businessDayKey = getPlannerBusinessHoursDayKey(dayKey);
  const businessDay = businessDayKey ? businessHours?.weekly?.[businessDayKey] : null;
  if (!businessDay) {
    return defaultWorkingHours;
  }

  if (!businessDay.enabled) {
    return {
      source: 'businessHours',
      isOpenDay: false,
      startTime: null,
      endTime: null,
      startMinutes: null,
      endMinutes: null,
      label: 'Godziny pracy: dzień oznaczony jako nieczynny',
    };
  }

  const startMinutes = parsePlannerTimeValueToMinutes(businessDay.open);
  const endMinutes = parsePlannerTimeValueToMinutes(businessDay.close);
  if (
    !businessDay.open ||
    !businessDay.close ||
    startMinutes === null ||
    endMinutes === null ||
    startMinutes >= endMinutes
  ) {
    return defaultWorkingHours;
  }

  return {
    source: 'businessHours',
    isOpenDay: true,
    startTime: businessDay.open,
    endTime: businessDay.close,
    startMinutes,
    endMinutes,
    label: `Godziny pracy: ${businessDay.open}-${businessDay.close}`,
  };
};

const isPlannerIntervalOutsideWorkingHours = (
  startMinutes: number | null,
  endMinutes: number | null,
  workingHours: PlannerWorkingHoursForDay
): boolean => {
  if (startMinutes === null) {
    return false;
  }
  if (!workingHours.isOpenDay) {
    return true;
  }
  if (workingHours.startMinutes === null || workingHours.endMinutes === null) {
    return false;
  }
  const effectiveEndMinutes = endMinutes !== null && endMinutes > startMinutes ? endMinutes : null;
  return (
    startMinutes < workingHours.startMinutes ||
    startMinutes >= workingHours.endMinutes ||
    (effectiveEndMinutes !== null && effectiveEndMinutes > workingHours.endMinutes)
  );
};

const getPlannerWorkingHoursBandStyle = (
  timeGrid: ServicePlannerDayTimeGrid,
  workingHours: PlannerWorkingHoursForDay
): React.CSSProperties | null => {
  if (
    !workingHours.isOpenDay ||
    workingHours.startMinutes === null ||
    workingHours.endMinutes === null
  ) {
    return null;
  }
  const rangeStartMinutes = timeGrid.rangeStartHour * 60;
  const rangeEndMinutes = timeGrid.rangeEndHour * 60;
  const bandStartMinutes = Math.max(workingHours.startMinutes, rangeStartMinutes);
  const bandEndMinutes = Math.min(workingHours.endMinutes, rangeEndMinutes);
  if (bandEndMinutes <= bandStartMinutes) {
    return null;
  }

  return {
    top: `${((bandStartMinutes - rangeStartMinutes) / 60) * SERVICE_PLANNER_HOUR_ROW_HEIGHT_PX}px`,
    height: `${((bandEndMinutes - bandStartMinutes) / 60) * SERVICE_PLANNER_HOUR_ROW_HEIGHT_PX}px`,
  };
};

const getPlannerDropSlotKey = (dayKey: string, startTime?: string): string =>
  startTime ? `${dayKey}T${startTime}` : dayKey;

const getPlannerTimeBlockStyle = (block: ServicePlannerTimeGridBlock): React.CSSProperties => ({
  top: `${(block.topOffsetMinutes / 60) * SERVICE_PLANNER_HOUR_ROW_HEIGHT_PX}px`,
  height: `${(block.visualDurationMinutes / 60) * SERVICE_PLANNER_HOUR_ROW_HEIGHT_PX}px`,
  minHeight: '54px',
});

const formatPlannerDayLabel = (dayKey: string): string => {
  const date = dateFromServicePlannerDayKey(dayKey);
  return Number.isFinite(date.getTime()) ? plannerLongDateFormatter.format(date) : dayKey;
};

const formatPlannerShortDayLabel = (dayKey: string): string => {
  const date = dateFromServicePlannerDayKey(dayKey);
  return Number.isFinite(date.getTime()) ? plannerDateFormatter.format(date) : dayKey;
};

const formatPlannerWeekdayLabel = (dayKey: string): string => {
  const date = dateFromServicePlannerDayKey(dayKey);
  return Number.isFinite(date.getTime())
    ? plannerWeekdayShortFormatter.format(date).replace('.', '')
    : dayKey;
};

const formatPlannerMonthYearLabel = (dayKey: string): string => {
  const date = dateFromServicePlannerDayKey(dayKey);
  return Number.isFinite(date.getTime()) ? plannerMonthYearFormatter.format(date) : dayKey;
};

const formatPlannerWeekRangeLabel = (weekItems: ServicePlannerWeekDay[]): string => {
  const first = weekItems[0]?.date;
  const last = weekItems[weekItems.length - 1]?.date;

  if (!first || !last) {
    return 'Tydzień';
  }

  const firstMonth = first.getUTCMonth();
  const lastMonth = last.getUTCMonth();
  const firstYear = first.getUTCFullYear();
  const lastYear = last.getUTCFullYear();

  if (firstMonth === lastMonth && firstYear === lastYear) {
    return `${plannerDayOnlyFormatter.format(first)}-${plannerDayMonthFormatter.format(last)}`;
  }

  return `${plannerDayMonthFormatter.format(first)}-${plannerDayMonthFormatter.format(last)}`;
};

const getMonthKeyFromDayKey = (dayKey: string): string => dayKey.slice(0, 7);

const getDaysInMonth = (year: number, monthIndex: number): number =>
  new Date(Date.UTC(year, monthIndex + 1, 0, 12)).getUTCDate();

const addMonthsToServicePlannerDayKey = (dayKey: string, months: number): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!match) {
    return dayKey;
  }

  const [, yearRaw, monthRaw, dayRaw] = match;
  const year = Number(yearRaw);
  const monthIndex = Number(monthRaw) - 1 + months;
  const day = Number(dayRaw);
  const firstOfTargetMonth = new Date(Date.UTC(year, monthIndex, 1, 12, 0, 0, 0));
  const boundedDay = Math.min(
    day,
    getDaysInMonth(firstOfTargetMonth.getUTCFullYear(), firstOfTargetMonth.getUTCMonth())
  );

  return getServicePlannerDayKey(
    new Date(
      Date.UTC(
        firstOfTargetMonth.getUTCFullYear(),
        firstOfTargetMonth.getUTCMonth(),
        boundedDay,
        12,
        0,
        0,
        0
      )
    )
  );
};

const getServicePlannerMonthGridDayKeys = (selectedDayKey: string): string[] => {
  const monthKey = getMonthKeyFromDayKey(selectedDayKey);
  const firstOfMonthKey = `${monthKey}-01`;
  const firstGridDayKey = getServicePlannerWeekDayKeys(firstOfMonthKey)[0] || firstOfMonthKey;

  return Array.from({ length: 42 }, (_, index) =>
    addDaysToServicePlannerDayKey(firstGridDayKey, index)
  );
};

const getDateTimePart = (date: Date, partType: Intl.DateTimeFormatPartTypes): string =>
  plannerInputDateTimeFormatter.formatToParts(date).find((part) => part.type === partType)?.value ||
  '';

const formatDateTimeForInputs = (
  date: Date | null
): Pick<PlannerFormValues, 'date' | 'startTime'> => {
  if (!date) {
    return { date: '', startTime: '' };
  }

  const year = getDateTimePart(date, 'year');
  const month = getDateTimePart(date, 'month');
  const day = getDateTimePart(date, 'day');
  const hour = getDateTimePart(date, 'hour');
  const minute = getDateTimePart(date, 'minute');

  if (!year || !month || !day || !hour || !minute) {
    return { date: '', startTime: '' };
  }

  return {
    date: `${year}-${month}-${day}`,
    startTime: `${hour}:${minute}`,
  };
};

const getTimeZoneOffsetMs = (date: Date): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SERVICE_PLANNER_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const pick = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value || NaN);

  const asUtc = Date.UTC(
    pick('year'),
    pick('month') - 1,
    pick('day'),
    pick('hour'),
    pick('minute'),
    pick('second')
  );

  return asUtc - date.getTime();
};

const buildWarsawDateTime = (dateValue: string, timeValue: string): Date | null => {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeValue);

  if (!dateMatch || !timeMatch) {
    return null;
  }

  const [, yearRaw, monthRaw, dayRaw] = dateMatch;
  const [, hourRaw, minuteRaw] = timeMatch;
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }

  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));
  const offsetMs = getTimeZoneOffsetMs(utcGuess);
  const date = new Date(utcGuess.getTime() - offsetMs);

  return Number.isFinite(date.getTime()) ? date : null;
};

const getPlannerFormSchedulePreview = (
  activeRequestId: string,
  values: PlannerFormValues,
  scheduledItems: ServicePlannerItem[],
  workloadSummaryByDay: Record<string, ServicePlannerDayWorkloadSummary>,
  businessHours: BusinessHours | null
): PlannerFormSchedulePreview => {
  if (!values.date || !values.startTime) {
    return EMPTY_PLANNER_FORM_SCHEDULE_PREVIEW;
  }

  const scheduledStartAt = buildWarsawDateTime(values.date, values.startTime);
  const scheduledEndAt =
    values.endTime && scheduledStartAt ? buildWarsawDateTime(values.date, values.endTime) : null;
  const workingHours = getPlannerWorkingHoursForDay(values.date, businessHours);
  const startMinutes = parsePlannerTimeValueToMinutes(values.startTime);
  const endMinutes = parsePlannerTimeValueToMinutes(values.endTime);

  if (!scheduledStartAt) {
    return {
      ...EMPTY_PLANNER_FORM_SCHEDULE_PREVIEW,
      workingHoursLabel: workingHours.label,
      workingHoursSource: workingHours.source,
    };
  }

  const candidateStartMs = scheduledStartAt.getTime();
  const candidateEndMs =
    scheduledEndAt && scheduledEndAt.getTime() > candidateStartMs ? scheduledEndAt.getTime() : null;

  const hasScheduleConflict = scheduledItems.some((item) => {
    if (item.requestId === activeRequestId || !item.scheduledStartAt) {
      return false;
    }

    const existingStartMs = item.scheduledStartAt.getTime();
    const existingDayKey = getServicePlannerDayKey(item.scheduledStartAt);
    const existingEndMs =
      item.scheduledEndAt &&
      item.scheduledEndAt.getTime() > existingStartMs &&
      getServicePlannerDayKey(item.scheduledEndAt) === existingDayKey
        ? item.scheduledEndAt.getTime()
        : null;

    if (existingDayKey !== values.date) {
      return false;
    }
    if (existingStartMs === candidateStartMs) {
      return true;
    }
    if (candidateEndMs && existingEndMs) {
      return candidateStartMs < existingEndMs && existingStartMs < candidateEndMs;
    }
    if (candidateEndMs) {
      return existingStartMs > candidateStartMs && existingStartMs < candidateEndMs;
    }
    if (existingEndMs) {
      return candidateStartMs > existingStartMs && candidateStartMs < existingEndMs;
    }
    return false;
  });

  return {
    hasScheduleConflict,
    isHeavyDay: workloadSummaryByDay[values.date]?.loadLevel === 'heavy',
    isOutsideWorkingHours: isPlannerIntervalOutsideWorkingHours(
      startMinutes,
      endMinutes,
      workingHours
    ),
    workingHoursLabel: workingHours.label,
    workingHoursSource: workingHours.source,
  };
};

const getInitialPlannerFormValues = (
  item: ServicePlannerItem,
  mode: PlannerActionMode
): PlannerFormValues => {
  if (mode !== 'reschedule') {
    return { ...EMPTY_PLANNER_FORM_VALUES };
  }

  const startInput = formatDateTimeForInputs(item.scheduledStartAt);
  const endInput = formatDateTimeForInputs(item.scheduledEndAt);
  const schedule =
    item.request.serviceSchedule?.status === 'scheduled' ? item.request.serviceSchedule : null;

  return {
    date: startInput.date,
    startTime: startInput.startTime,
    endTime: endInput.startTime,
    note: schedule?.note || '',
  };
};

const getPrefilledPlannerFormValues = (
  item: ServicePlannerItem,
  mode: PlannerActionMode,
  prefilledDayKey?: string,
  prefilledStartTime?: string
): PlannerFormValues => {
  const initialValues = getInitialPlannerFormValues(item, mode);

  if (!prefilledDayKey) {
    return initialValues;
  }

  let shiftedEndTime = '';
  const shouldShiftExistingEnd =
    mode === 'reschedule' &&
    Boolean(prefilledStartTime) &&
    item.scheduledStartAt instanceof Date &&
    item.scheduledEndAt instanceof Date &&
    item.scheduledEndAt.getTime() > item.scheduledStartAt.getTime();

  if (shouldShiftExistingEnd && prefilledStartTime) {
    const nextStartAt = buildWarsawDateTime(prefilledDayKey, prefilledStartTime);
    if (nextStartAt && item.scheduledStartAt && item.scheduledEndAt) {
      const durationMs = item.scheduledEndAt.getTime() - item.scheduledStartAt.getTime();
      const nextEndInput = formatDateTimeForInputs(new Date(nextStartAt.getTime() + durationMs));
      shiftedEndTime = nextEndInput.date === prefilledDayKey ? nextEndInput.startTime : '';
    }
  }

  return {
    ...initialValues,
    date: prefilledDayKey,
    startTime: prefilledStartTime || '',
    endTime: shiftedEndTime,
  };
};

const getPlannerErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

const buildPlannerDetailHref = (requestId: string): string => {
  const params = new URLSearchParams();
  params.set('returnTo', '/app/service/planner');
  params.set('workspace', 'service');
  return `/app/request/${requestId}?${params.toString()}`;
};

const getDeviceMeta = (request: RepairRequest): string => {
  const parts = [request.brand, request.model]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Brak modelu w zgłoszeniu';
};

const getClientLabel = (request: RepairRequest): string => {
  const details = request.clientDetails;
  const displayName = String(details?.displayName || '').trim();
  const email = String(details?.email || '').trim();

  return displayName || email || 'Klient zlecenia';
};

interface PlannerReadableEventParts {
  time: string;
  subject: string;
  model: string;
  client: string;
  status: string;
}

const getPlannerReadableEventParts = (item: ServicePlannerItem): PlannerReadableEventParts => {
  const request = item.request;

  return {
    time: formatPlannerTime(item.scheduledStartAt) || 'bez godziny',
    subject: getRepairRequestSubjectLabel(request),
    model: getDeviceMeta(request),
    client: getClientLabel(request),
    status: getRepairRequestStatusLabel(request.status),
  };
};

const buildPlannerReadableEventLine = (parts: PlannerReadableEventParts): string =>
  [parts.time, parts.subject, parts.model, parts.client].filter(Boolean).join(' · ');

const buildPlannerReadableEventMobileLead = (parts: PlannerReadableEventParts): string =>
  [parts.time, parts.subject].filter(Boolean).join(' · ');

const buildPlannerReadableEventMobileDetail = (parts: PlannerReadableEventParts): string =>
  [parts.model, parts.client].filter(Boolean).join(' · ');

const isPlannerDragViewport = (): boolean =>
  typeof window !== 'undefined' && window.innerWidth >= PLANNER_DRAG_MIN_WIDTH_PX;

const getPlannerDecisionViewport = (): PlannerDecisionViewport =>
  typeof window !== 'undefined' && window.innerWidth >= PLANNER_DESKTOP_LAYOUT_MIN_WIDTH_PX
    ? 'desktop'
    : 'mobile';

const isPlannerMobileViewport = (): boolean =>
  typeof window !== 'undefined' && window.innerWidth < PLANNER_DESKTOP_LAYOUT_MIN_WIDTH_PX;

const getInitialPlannerViewMode = (): PlannerViewMode =>
  isPlannerMobileViewport() ? 'week' : 'day';

const focusPlannerDecisionTarget = (target: HTMLElement) => {
  try {
    target.focus({ preventScroll: true });
  } catch (_error) {
    target.focus();
  }
};

const usePlannerDragViewport = (): boolean => {
  const [isEnabled, setIsEnabled] = useState(isPlannerDragViewport);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncDragViewport = () => setIsEnabled(isPlannerDragViewport());
    const mediaQuery = window.matchMedia?.(`(min-width: ${PLANNER_DRAG_MIN_WIDTH_PX}px)`);

    syncDragViewport();

    if (mediaQuery) {
      mediaQuery.addEventListener?.('change', syncDragViewport);
      mediaQuery.addListener?.(syncDragViewport);

      return () => {
        mediaQuery.removeEventListener?.('change', syncDragViewport);
        mediaQuery.removeListener?.(syncDragViewport);
      };
    }

    window.addEventListener('resize', syncDragViewport);
    return () => window.removeEventListener('resize', syncDragViewport);
  }, []);

  return isEnabled;
};

const createPlannerDragGhost = (item: ServicePlannerItem): HTMLElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const ghost = document.createElement('div');
  ghost.style.position = 'fixed';
  ghost.style.top = '-1000px';
  ghost.style.left = '-1000px';
  ghost.style.maxWidth = '260px';
  ghost.style.padding = '8px 10px';
  ghost.style.border = '1px solid rgb(var(--rs-color-border-subtle) / 0.24)';
  ghost.style.borderRadius = '8px';
  ghost.style.background = 'rgb(var(--rs-color-surface-panel) / 0.96)';
  ghost.style.color = 'rgb(var(--rs-color-text-primary))';
  ghost.style.boxShadow = '0 12px 30px rgb(0 0 0 / 0.32)';
  ghost.style.font = '600 12px system-ui, sans-serif';
  ghost.style.pointerEvents = 'none';
  ghost.style.zIndex = '9999';

  const subject = getRepairRequestSubjectLabel(item.request);
  const index = getRepairRequestOperationalIndexLabel(item.request);
  const meta = getDeviceMeta(item.request);
  ghost.textContent = `${index} · ${subject} · ${meta}`;

  document.body.appendChild(ghost);
  return ghost;
};

const ServicePlannerPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState<RepairRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [activeAction, setActiveAction] = useState<ActivePlannerAction | null>(null);
  const [formValues, setFormValues] = useState<PlannerFormValues>(EMPTY_PLANNER_FORM_VALUES);
  const [formErrors, setFormErrors] = useState<PlannerFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clearState, setClearState] = useState<ClearPlannerState | null>(null);
  const [viewMode, setViewMode] = useState<PlannerViewMode>(getInitialPlannerViewMode);
  const [selectedDayKey, setSelectedDayKey] = useState(() => getServicePlannerDayKey(new Date()));
  const [dayDetailsDayKey, setDayDetailsDayKey] = useState<string | null>(null);
  const [activeSlot, setActiveSlot] = useState<ActivePlannerSlot | null>(null);
  const [draggedRequestId, setDraggedRequestId] = useState<string | null>(null);
  const [dragOverDayKey, setDragOverDayKey] = useState<string | null>(null);
  const [dragOverSlotKey, setDragOverSlotKey] = useState<string | null>(null);
  const [decisionAnchor, setDecisionAnchor] = useState<PlannerDecisionAnchor | null>(null);
  const [serviceBusinessHours, setServiceBusinessHours] = useState<BusinessHours | null>(null);
  const isPlannerDragEnabled = usePlannerDragViewport();
  const hasReceivedSnapshotRef = useRef(false);
  const consumedPlanRequestIdRef = useRef<string | null>(null);
  const decisionAnchorNonceRef = useRef(0);
  const planRequestId = searchParams.get('planRequestId')?.trim() || null;

  const requestDecisionAnchor = useCallback(
    (kind: PlannerDecisionAnchor['kind'], requestId: string) => {
      decisionAnchorNonceRef.current += 1;
      setDecisionAnchor({ kind, requestId, nonce: decisionAnchorNonceRef.current });
    },
    []
  );

  const clearPlanRequestParam = useCallback(() => {
    if (!searchParams.has('planRequestId')) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('planRequestId');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!planRequestId) {
      consumedPlanRequestIdRef.current = null;
    }
  }, [planRequestId]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setRequests([]);
      setIsLoading(false);
      setError('Musisz być zalogowany jako serwis, aby otworzyć planer pracy.');
      hasReceivedSnapshotRef.current = false;
      setServiceBusinessHours(null);
      return;
    }

    hasReceivedSnapshotRef.current = false;
    setIsLoading(true);
    setError(null);

    const unsubscribe = repairRequestService.subscribeToRequestsForServiceProvider(
      currentUser.uid,
      'all',
      'updatedAt',
      (nextRequests) => {
        setRequests(
          nextRequests.filter((request) =>
            isRepairRequestAssignedToServiceUser(request, currentUser.uid)
          )
        );
        hasReceivedSnapshotRef.current = true;
        setError(null);
        setIsLoading(false);
      },
      (subscriptionError) => {
        console.error('Błąd subskrypcji planera pracy serwisu:', subscriptionError);
        if (!hasReceivedSnapshotRef.current) {
          setError(
            'Nie udało się pobrać zleceń do planera. Sprawdź połączenie i spróbuj ponownie.'
          );
          setIsLoading(false);
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [currentUser?.uid, retryNonce]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setServiceBusinessHours(null);
      return undefined;
    }

    let isMounted = true;
    setServiceBusinessHours(null);

    serviceProviderService
      .getServiceProviderProfile(currentUser.uid)
      .then((profile) => {
        if (!isMounted) {
          return;
        }
        setServiceBusinessHours(normalizeBusinessHours(profile?.businessHours));
      })
      .catch(() => {
        if (isMounted) {
          setServiceBusinessHours(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [currentUser?.uid]);

  const planner = useMemo(
    () => deriveServicePlannerItems(requests, { selectedDayKey }),
    [requests, selectedDayKey]
  );
  const requestById = useMemo(() => {
    const items = new Map<string, RepairRequest>();
    requests.forEach((request) => {
      const requestId = getRepairRequestId(request);
      if (requestId && !items.has(requestId)) {
        items.set(requestId, request);
      }
    });
    return items;
  }, [requests]);
  const plannerItemById = useMemo(() => {
    const items = new Map<string, ServicePlannerItem>();
    [...planner.scheduledItems, ...planner.unscheduledItems].forEach((item) => {
      if (!items.has(item.requestId)) {
        items.set(item.requestId, item);
      }
    });
    return items;
  }, [planner.scheduledItems, planner.unscheduledItems]);
  const activePlannerItem = activeAction
    ? plannerItemById.get(activeAction.requestId) || null
    : null;
  const draggedPlannerItem = draggedRequestId
    ? plannerItemById.get(draggedRequestId) || null
    : null;
  const canDropDraggedPlannerItem = isPlannerDragEnabled && Boolean(draggedPlannerItem);
  const canUsePlannerDayDropTargets =
    canDropDraggedPlannerItem &&
    Boolean(draggedPlannerItem && !draggedPlannerItem.scheduledStartAt);
  const canUsePlannerSlotDropTargets = canDropDraggedPlannerItem;
  const activeActionUnavailableMessage = useMemo(() => {
    if (!activeAction) {
      return null;
    }
    if (!activePlannerItem) {
      return 'Zlecenie zniknęło z aktywnego planera albo zostało zakończone.';
    }
    if (activeAction.mode === 'schedule' && activePlannerItem.scheduledStartAt) {
      return 'Zlecenie ma już termin pracy serwisu. Odśwież wybór i użyj przełożenia.';
    }
    if (activeAction.mode === 'reschedule' && !activePlannerItem.scheduledStartAt) {
      return 'Ten termin nie jest już aktywny. Zlecenie wróciło do listy do zaplanowania.';
    }
    return null;
  }, [activeAction, activePlannerItem]);
  const formSchedulePreview = useMemo(
    () =>
      activeAction
        ? getPlannerFormSchedulePreview(
            activeAction.requestId,
            formValues,
            planner.scheduledItems,
            planner.workloadSummaryByDay,
            serviceBusinessHours
          )
        : EMPTY_PLANNER_FORM_SCHEDULE_PREVIEW,
    [
      activeAction,
      formValues,
      planner.scheduledItems,
      planner.workloadSummaryByDay,
      serviceBusinessHours,
    ]
  );
  const selectedDayWorkingHours = useMemo(
    () => getPlannerWorkingHoursForDay(planner.selectedDayKey, serviceBusinessHours),
    [planner.selectedDayKey, serviceBusinessHours]
  );

  useEffect(() => {
    if (!draggedRequestId) {
      return;
    }
    if (!isPlannerDragEnabled || !plannerItemById.has(draggedRequestId)) {
      setDraggedRequestId(null);
      setDragOverDayKey(null);
      setDragOverSlotKey(null);
    }
  }, [draggedRequestId, isPlannerDragEnabled, plannerItemById]);

  useEffect(() => {
    if (!decisionAnchor || typeof document === 'undefined') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const viewport = getPlannerDecisionViewport();
      const anchors = Array.from(
        document.querySelectorAll<HTMLElement>(
          `[data-planner-decision-anchor="${decisionAnchor.kind}"][data-planner-request-id]`
        )
      );
      const anchor =
        anchors.find(
          (candidate) =>
            candidate.dataset.plannerRequestId === decisionAnchor.requestId &&
            candidate.dataset.plannerViewport === viewport
        ) ||
        anchors.find(
          (candidate) => candidate.dataset.plannerRequestId === decisionAnchor.requestId
        );

      if (!anchor) {
        return;
      }

      anchor.scrollIntoView?.({ block: 'start' });
      const focusTarget =
        anchor.querySelector<HTMLElement>('[data-planner-focus-target="true"]') || anchor;
      focusPlannerDecisionTarget(focusTarget);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [decisionAnchor]);

  const hasPlannerItems =
    planner.scheduledItems.length > 0 ||
    planner.unscheduledItems.length > 0 ||
    planner.clientPreferenceItems.length > 0;
  const deepLinkState = useMemo<PlannerDeepLinkState>(() => {
    if (!planRequestId || isLoading) {
      return null;
    }

    const request = requestById.get(planRequestId) || null;
    if (!request) {
      return {
        kind: 'missing',
        requestId: planRequestId,
      };
    }

    if (isTerminalRepairRequestStatus(request.status)) {
      return {
        kind: 'terminal',
        requestId: planRequestId,
      };
    }

    const item = plannerItemById.get(planRequestId) || null;
    if (!item) {
      return {
        kind: 'missing',
        requestId: planRequestId,
      };
    }

    return {
      kind: 'ready',
      requestId: planRequestId,
      item,
      mode: item.scheduledStartAt ? 'reschedule' : 'schedule',
    };
  }, [isLoading, planRequestId, plannerItemById, requestById]);

  const closePlannerForm = useCallback(() => {
    setActiveAction(null);
    setFormValues(EMPTY_PLANNER_FORM_VALUES);
    setFormErrors({});
    setSubmitError(null);
    setIsSubmitting(false);
    clearPlanRequestParam();
  }, [clearPlanRequestParam]);

  const openPlannerForm = useCallback(
    (
      item: ServicePlannerItem,
      mode: PlannerActionMode,
      options: {
        source?: PlannerActionSource;
        prefilledDayKey?: string;
        prefilledStartTime?: string;
      } = {}
    ) => {
      const slotPrefill =
        mode === 'schedule' && !options.prefilledDayKey && activeSlot ? activeSlot : null;
      const selectedDayPrefill =
        mode === 'schedule' ? selectedDayKey || getServicePlannerDayKey(new Date()) : undefined;
      const prefilledDayKey = options.prefilledDayKey || slotPrefill?.dayKey || selectedDayPrefill;
      const prefilledStartTime = options.prefilledStartTime || slotPrefill?.startTime || '';

      if (prefilledDayKey) {
        setSelectedDayKey(prefilledDayKey);
        setViewMode('day');
      } else if (mode === 'reschedule' && item.scheduledStartAt) {
        setSelectedDayKey(getServicePlannerDayKey(item.scheduledStartAt));
        setViewMode('day');
      }

      setActiveAction({
        requestId: item.requestId,
        mode,
        source: options.source || (slotPrefill ? 'timeSlot' : 'manual'),
        prefilledDayKey,
        prefilledStartTime,
      });
      setFormValues(getPrefilledPlannerFormValues(item, mode, prefilledDayKey, prefilledStartTime));
      setFormErrors({});
      setSubmitError(null);
      requestDecisionAnchor('form', item.requestId);
    },
    [activeSlot, requestDecisionAnchor, selectedDayKey]
  );

  const handlePlannerItemDragStart = useCallback(
    (event: React.DragEvent<HTMLElement>, item: ServicePlannerItem) => {
      if (!isPlannerDragEnabled) {
        event.preventDefault();
        return;
      }

      event.dataTransfer.effectAllowed = item.scheduledStartAt ? 'move' : 'copy';
      event.dataTransfer.setData(SERVICE_PLANNER_DRAG_MIME, item.requestId);
      event.dataTransfer.setData('text/plain', item.requestId);
      const ghost = createPlannerDragGhost(item);
      if (ghost) {
        event.dataTransfer.setDragImage(ghost, 14, 14);
        window.setTimeout(() => ghost.remove(), 0);
      }
      setDraggedRequestId(item.requestId);
      setDragOverDayKey(null);
      setDragOverSlotKey(null);
    },
    [isPlannerDragEnabled]
  );

  const handlePlannerItemDragEnd = useCallback(() => {
    setDraggedRequestId(null);
    setDragOverDayKey(null);
    setDragOverSlotKey(null);
  }, []);

  const handlePlannerDayDragOver = useCallback(
    (event: React.DragEvent<HTMLElement>, dayKey: string, startTime?: string) => {
      if (!isPlannerDragEnabled || !draggedPlannerItem) {
        return;
      }

      const isExistingEventDrag = Boolean(draggedPlannerItem.scheduledStartAt);
      if (isExistingEventDrag) {
        const sourceDayKey = draggedPlannerItem.scheduledStartAt
          ? getServicePlannerDayKey(draggedPlannerItem.scheduledStartAt)
          : null;
        if (!startTime || sourceDayKey !== dayKey) {
          return;
        }
      }

      if (!isExistingEventDrag && !canDropDraggedPlannerItem) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = isExistingEventDrag ? 'move' : 'copy';
      setDragOverDayKey(dayKey);
      setDragOverSlotKey(startTime ? getPlannerDropSlotKey(dayKey, startTime) : null);
    },
    [canDropDraggedPlannerItem, draggedPlannerItem, isPlannerDragEnabled]
  );

  const handlePlannerDayDragLeave = useCallback(
    (event: React.DragEvent<HTMLElement>, dayKey: string, startTime?: string) => {
      const nextTarget = event.relatedTarget as Node | null;
      if (nextTarget && event.currentTarget.contains(nextTarget)) {
        return;
      }
      const slotKey = startTime ? getPlannerDropSlotKey(dayKey, startTime) : null;
      setDragOverDayKey((current) => (current === dayKey ? null : current));
      setDragOverSlotKey((current) => (current === slotKey ? null : current));
    },
    []
  );

  const handlePlannerDayDrop = useCallback(
    (event: React.DragEvent<HTMLElement>, dayKey: string, startTime?: string) => {
      if (!isPlannerDragEnabled) {
        return;
      }

      const droppedRequestId =
        event.dataTransfer.getData(SERVICE_PLANNER_DRAG_MIME) ||
        event.dataTransfer.getData('text/plain') ||
        draggedRequestId;
      const item = droppedRequestId ? plannerItemById.get(droppedRequestId) || null : null;

      setDraggedRequestId(null);
      setDragOverDayKey(null);
      setDragOverSlotKey(null);

      if (!item) {
        return;
      }

      if (item.scheduledStartAt) {
        const sourceDayKey = getServicePlannerDayKey(item.scheduledStartAt);
        if (!startTime || sourceDayKey !== dayKey) {
          return;
        }

        event.preventDefault();
        openPlannerForm(item, 'reschedule', {
          source: 'calendarDrop',
          prefilledDayKey: dayKey,
          prefilledStartTime: startTime,
        });
        return;
      }

      if (!canDropDraggedPlannerItem) {
        return;
      }

      event.preventDefault();
      openPlannerForm(item, 'schedule', {
        source: 'calendarDrop',
        prefilledDayKey: dayKey,
        prefilledStartTime: startTime,
      });
    },
    [
      canDropDraggedPlannerItem,
      draggedRequestId,
      isPlannerDragEnabled,
      openPlannerForm,
      plannerItemById,
    ]
  );

  const dragSourceConfig = useMemo<PlannerDragSourceConfig>(
    () => ({
      isEnabled: isPlannerDragEnabled,
      draggingRequestId: draggedRequestId,
      onDragStart: handlePlannerItemDragStart,
      onDragEnd: handlePlannerItemDragEnd,
    }),
    [draggedRequestId, handlePlannerItemDragEnd, handlePlannerItemDragStart, isPlannerDragEnabled]
  );

  const dropTargetConfig = useMemo<PlannerDropTargetConfig>(
    () => ({
      isEnabled: canDropDraggedPlannerItem,
      isDayEnabled: canUsePlannerDayDropTargets,
      isSlotEnabled: canUsePlannerSlotDropTargets,
      activeDayKey: dragOverDayKey,
      activeSlotKey: dragOverSlotKey,
      onDragOver: handlePlannerDayDragOver,
      onDragLeave: handlePlannerDayDragLeave,
      onDrop: handlePlannerDayDrop,
    }),
    [
      canDropDraggedPlannerItem,
      canUsePlannerDayDropTargets,
      canUsePlannerSlotDropTargets,
      dragOverDayKey,
      dragOverSlotKey,
      handlePlannerDayDragLeave,
      handlePlannerDayDragOver,
      handlePlannerDayDrop,
    ]
  );

  useEffect(() => {
    if (deepLinkState?.kind !== 'ready') {
      return;
    }
    if (consumedPlanRequestIdRef.current === deepLinkState.requestId) {
      return;
    }
    consumedPlanRequestIdRef.current = deepLinkState.requestId;
    openPlannerForm(deepLinkState.item, deepLinkState.mode);
  }, [deepLinkState, openPlannerForm]);

  const validatePlannerForm = (): {
    errors: PlannerFormErrors;
    scheduledStartAt: Date | null;
    scheduledEndAt: Date | null;
    note: string | null;
  } => {
    const errors: PlannerFormErrors = {};
    const note = formValues.note.trim();
    const scheduledStartAt =
      formValues.date && formValues.startTime
        ? buildWarsawDateTime(formValues.date, formValues.startTime)
        : null;
    const scheduledEndAt =
      formValues.date && formValues.endTime
        ? buildWarsawDateTime(formValues.date, formValues.endTime)
        : null;

    if (!formValues.date) {
      errors.date = 'Podaj datę pracy.';
    }
    if (!formValues.startTime) {
      errors.startTime = 'Podaj godzinę startu.';
    }
    if (formValues.date && formValues.startTime && !scheduledStartAt) {
      errors.startTime = 'Podaj poprawny początek terminu pracy.';
    }
    if (formValues.endTime && !scheduledEndAt) {
      errors.endTime = 'Podaj poprawną godzinę końca.';
    }
    if (
      scheduledStartAt &&
      scheduledEndAt &&
      scheduledEndAt.getTime() <= scheduledStartAt.getTime()
    ) {
      errors.endTime = 'Godzina końca musi być po starcie.';
    }
    if (note.length > SERVICE_SCHEDULE_NOTE_MAX_LENGTH) {
      errors.note = `Notatka może mieć maksymalnie ${SERVICE_SCHEDULE_NOTE_MAX_LENGTH} znaków.`;
    }

    return {
      errors,
      scheduledStartAt,
      scheduledEndAt,
      note: note || null,
    };
  };

  const handlePlannerFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!activeAction || !activePlannerItem) {
      setSubmitError('Zlecenie nie jest już dostępne w aktywnym planerze.');
      return;
    }
    if (activeActionUnavailableMessage) {
      setSubmitError(activeActionUnavailableMessage);
      return;
    }

    const validation = validatePlannerForm();
    setFormErrors(validation.errors);
    setSubmitError(null);

    if (Object.keys(validation.errors).length > 0 || !validation.scheduledStartAt) {
      return;
    }

    setIsSubmitting(true);
    try {
      await repairRequestService.scheduleRepairRequestServicePlan(activePlannerItem.requestId, {
        scheduledStartAt: validation.scheduledStartAt,
        scheduledEndAt: validation.scheduledEndAt,
        note: validation.note,
      });
      setSelectedDayKey(getServicePlannerDayKey(validation.scheduledStartAt));
      setActiveSlot(null);
      setViewMode('day');
      closePlannerForm();
    } catch (submitErrorValue) {
      setSubmitError(
        getPlannerErrorMessage(
          submitErrorValue,
          'Nie udało się zapisać terminu pracy. Odśwież widok i spróbuj ponownie.'
        )
      );
      setIsSubmitting(false);
    }
  };

  const openClearConfirm = useCallback(
    (requestId: string) => {
      setClearState({ requestId, isSubmitting: false, error: null });
      requestDecisionAnchor('clear', requestId);
    },
    [requestDecisionAnchor]
  );

  const handleClearConfirm = async (requestId: string) => {
    setClearState({ requestId, isSubmitting: true, error: null });

    try {
      await repairRequestService.clearRepairRequestServicePlan(requestId);
      setClearState(null);
      if (activeAction?.requestId === requestId) {
        closePlannerForm();
      }
    } catch (clearErrorValue) {
      setClearState({
        requestId,
        isSubmitting: false,
        error: getPlannerErrorMessage(
          clearErrorValue,
          'Nie udało się wyczyścić terminu pracy. Odśwież widok i spróbuj ponownie.'
        ),
      });
    }
  };

  const handlePlannerFormChange = (patch: Partial<PlannerFormValues>) => {
    setFormValues((current) => ({ ...current, ...patch }));
    setFormErrors((current) => {
      const next = { ...current };
      (Object.keys(patch) as Array<keyof PlannerFormValues>).forEach((key) => {
        if (key in next) {
          delete next[key as keyof PlannerFormErrors];
        }
      });
      return next;
    });
    setSubmitError(null);
  };

  const renderPlanningForm = (
    inlineItem?: ServicePlannerItem,
    anchorViewport: PlannerDecisionViewport = 'desktop'
  ) => {
    if (!activeAction) {
      return null;
    }

    const item = inlineItem || activePlannerItem;

    return (
      <PlannerScheduleForm
        mode={activeAction.mode}
        requestId={activeAction.requestId}
        item={item}
        unavailableMessage={activeActionUnavailableMessage}
        values={formValues}
        errors={formErrors}
        submitError={submitError}
        isSubmitting={isSubmitting}
        openedFromDrop={activeAction.source === 'calendarDrop'}
        schedulePreview={formSchedulePreview}
        anchorViewport={anchorViewport}
        onChange={handlePlannerFormChange}
        onCancel={closePlannerForm}
        onSubmit={handlePlannerFormSubmit}
      />
    );
  };

  const handleOpenDayDetails = useCallback((dayKey: string) => {
    setSelectedDayKey(dayKey);
    setActiveSlot(null);
    setDayDetailsDayKey(dayKey);
  }, []);

  const handleSelectWeekDay = useCallback((dayKey: string) => {
    setSelectedDayKey(dayKey);
    setActiveSlot(null);
    setDayDetailsDayKey(null);
    setViewMode('day');
  }, []);

  const handleCloseDayDetails = useCallback(() => {
    setDayDetailsDayKey(null);
  }, []);

  const handlePlanOnDay = useCallback((dayKey: string) => {
    setSelectedDayKey(dayKey);
    setActiveSlot(null);
    setDayDetailsDayKey(null);
    setViewMode('day');
  }, []);

  const handleTodayClick = useCallback(() => {
    setSelectedDayKey(getServicePlannerDayKey(new Date()));
    setActiveSlot(null);
    setDayDetailsDayKey(null);
    setViewMode((current) => (current === 'month' ? 'month' : 'day'));
  }, []);

  const handlePreviousDate = useCallback(() => {
    setSelectedDayKey((current) =>
      viewMode === 'month'
        ? addMonthsToServicePlannerDayKey(current, -1)
        : addDaysToServicePlannerDayKey(current, viewMode === 'week' ? -7 : -1)
    );
    setActiveSlot(null);
    setDayDetailsDayKey(null);
  }, [viewMode]);

  const handleNextDate = useCallback(() => {
    setSelectedDayKey((current) =>
      viewMode === 'month'
        ? addMonthsToServicePlannerDayKey(current, 1)
        : addDaysToServicePlannerDayKey(current, viewMode === 'week' ? 7 : 1)
    );
    setActiveSlot(null);
    setDayDetailsDayKey(null);
  }, [viewMode]);

  const handlePreviousMonth = useCallback(() => {
    setSelectedDayKey((current) => addMonthsToServicePlannerDayKey(current, -1));
    setActiveSlot(null);
    setDayDetailsDayKey(null);
  }, []);

  const handleNextMonth = useCallback(() => {
    setSelectedDayKey((current) => addMonthsToServicePlannerDayKey(current, 1));
    setActiveSlot(null);
    setDayDetailsDayKey(null);
  }, []);

  const handleTimeSlotClick = useCallback((dayKey: string, startTime: string) => {
    setSelectedDayKey(dayKey);
    setActiveSlot({ dayKey, startTime });
    setViewMode('day');
  }, []);

  const selectedDayLabel = formatPlannerDayLabel(planner.selectedDayKey);
  const activeDateLabel =
    viewMode === 'week'
      ? formatPlannerWeekRangeLabel(planner.weekItems)
      : viewMode === 'month'
        ? formatPlannerMonthYearLabel(planner.selectedDayKey)
        : selectedDayLabel;
  const desktopViewMode: PlannerViewMode = viewMode === 'month' ? 'day' : viewMode;
  const desktopActiveDateLabel =
    desktopViewMode === 'week' ? formatPlannerWeekRangeLabel(planner.weekItems) : selectedDayLabel;
  const dayDetailsItems = dayDetailsDayKey
    ? planner.scheduledItemsByDay[dayDetailsDayKey] || []
    : [];
  const dayDetailsWorkloadSummary = dayDetailsDayKey
    ? planner.workloadSummaryByDay[dayDetailsDayKey] || {
        dayKey: dayDetailsDayKey,
        scheduledCount: 0,
        firstScheduledAt: null,
        lastScheduledAt: null,
        conflictCount: 0,
        loadLevel: 'empty',
      }
    : null;
  const dayDetailsLabel = dayDetailsDayKey ? formatPlannerDayLabel(dayDetailsDayKey) : null;

  if (isLoading) {
    return (
      <ServiceWorkspaceShell activeSection="planner" containerClassName="max-w-7xl">
        <PageLoadingState
          title="Ładowanie planera pracy"
          message="Pobieramy przypisane zlecenia i układamy widok planowania."
        />
      </ServiceWorkspaceShell>
    );
  }

  if (error) {
    return (
      <ServiceWorkspaceShell activeSection="planner" containerClassName="max-w-3xl">
        <PageErrorState
          title="Nie udało się otworzyć planera"
          message={error}
          actionLabel="Spróbuj ponownie"
          onAction={() => setRetryNonce((value) => value + 1)}
        />
      </ServiceWorkspaceShell>
    );
  }

  return (
    <ServiceWorkspaceShell
      activeSection="planner"
      surfaceVariant="orders_focus"
      contentClassName="min-w-0 overflow-x-hidden pb-6"
    >
      <div className="min-w-0 space-y-3 pt-1 sm:space-y-4">
        <header className="min-w-0">
          <h1 className="sr-only">Planer pracy</h1>
        </header>

        <DeepLinkPlannerStateNotice state={deepLinkState} />

        {activeAction && activeActionUnavailableMessage ? (
          <div className="lg:hidden">{renderPlanningForm(undefined, 'mobile')}</div>
        ) : null}

        {!hasPlannerItems ? (
          <PageEmptyState
            title="Brak zleceń do planowania"
            message="Aktywne sprawy przypisane do serwisu pojawią się tutaj, gdy będzie można ułożyć dla nich pracę."
          />
        ) : (
          <>
            <div
              data-planner-surface="zone"
              className={`${PLANNER_ZONE_SURFACE_CLASS} hidden min-w-0 gap-4 lg:grid lg:grid-cols-[minmax(260px,0.5fr)_minmax(0,1.58fr)_minmax(240px,0.42fr)] xl:grid-cols-[minmax(300px,340px)_minmax(620px,1fr)_minmax(260px,320px)] 2xl:grid-cols-[minmax(320px,360px)_minmax(720px,1fr)_minmax(280px,340px)]`}
            >
              <aside className="min-w-0 space-y-3">
                <PlannerPanel
                  title="Do zaplanowania"
                  items={planner.unscheduledItems}
                  emptyTitle="Brak spraw do zaplanowania."
                  emptyMessage=""
                  dateMode="preference"
                  actionScope="unscheduled"
                  activeAction={activeAction}
                  clearState={clearState}
                  onOpenPlannerForm={openPlannerForm}
                  onRequestClearConfirm={openClearConfirm}
                  onCancelClear={() => setClearState(null)}
                  onConfirmClear={handleClearConfirm}
                  renderPlanningForm={renderPlanningForm}
                  dragSource={dragSourceConfig}
                />
                {planner.clientPreferenceItems.length > 0 ? (
                  <PlannerPanel
                    title="Preferencje klienta"
                    items={planner.clientPreferenceItems}
                    emptyTitle="Brak preferencji klienta"
                    emptyMessage="Ta sekcja pokazuje tylko sugestie z pola preferowanego terminu."
                    dateMode="preference"
                    actionScope="none"
                    activeAction={activeAction}
                    clearState={clearState}
                    onOpenPlannerForm={openPlannerForm}
                    onRequestClearConfirm={openClearConfirm}
                    onCancelClear={() => setClearState(null)}
                    onConfirmClear={handleClearConfirm}
                    renderPlanningForm={renderPlanningForm}
                    dragSource={dragSourceConfig}
                    quiet
                  />
                ) : null}
              </aside>

              <main className="rs-planner-main-canvas">
                <PlannerDateHeader
                  viewMode={desktopViewMode}
                  variant="desktop"
                  activeDateLabel={desktopActiveDateLabel}
                  onPrevious={handlePreviousDate}
                  onNext={handleNextDate}
                  onToday={handleTodayClick}
                  onViewModeChange={setViewMode}
                />

                {desktopViewMode === 'day' ? (
                  <PlannerDayAgenda
                    selectedDayKey={planner.selectedDayKey}
                    selectedDayLabel={selectedDayLabel}
                    items={planner.selectedDayItems}
                    workloadSummary={planner.selectedDayWorkloadSummary}
                    timeGrid={planner.selectedDayTimeGrid}
                    workingHours={selectedDayWorkingHours}
                    activeSlot={activeSlot}
                    activeAction={activeAction}
                    clearState={clearState}
                    onOpenPlannerForm={openPlannerForm}
                    onTimeSlotClick={handleTimeSlotClick}
                    onRequestClearConfirm={openClearConfirm}
                    onCancelClear={() => setClearState(null)}
                    onConfirmClear={handleClearConfirm}
                    renderPlanningForm={renderPlanningForm}
                    dragSource={dragSourceConfig}
                    dropTarget={dropTargetConfig}
                  />
                ) : null}

                {desktopViewMode === 'week' ? (
                  <PlannerWeekOverview
                    variant="desktop"
                    weekItems={planner.weekItems}
                    onSelectWeekDay={handleSelectWeekDay}
                    onOpenDayDetails={handleOpenDayDetails}
                    activeAction={activeAction}
                    clearState={clearState}
                    onOpenPlannerForm={openPlannerForm}
                    onRequestClearConfirm={openClearConfirm}
                    onCancelClear={() => setClearState(null)}
                    onConfirmClear={handleClearConfirm}
                    dropTarget={dropTargetConfig}
                  />
                ) : null}
              </main>

              <aside className="rs-planner-right-rail">
                <PlannerSideContext
                  selectedDayKey={planner.selectedDayKey}
                  selectedDayLabel={selectedDayLabel}
                  selectedDayItems={planner.selectedDayItems}
                  selectedDayWorkloadSummary={planner.selectedDayWorkloadSummary}
                  weekItems={planner.weekItems}
                  scheduledCountsByDay={planner.scheduledCountsByDay}
                  clientPreferenceCountsByDay={planner.clientPreferenceCountsByDay}
                  workloadSummaryByDay={planner.workloadSummaryByDay}
                  onSelectDay={handleOpenDayDetails}
                  onPreviousMonth={handlePreviousMonth}
                  onNextMonth={handleNextMonth}
                  dropTarget={dropTargetConfig}
                />
              </aside>
            </div>

            <div
              data-testid="service-planner-mobile-flow"
              data-planner-surface="zone"
              className={`${PLANNER_ZONE_SURFACE_CLASS} min-w-0 space-y-3 overflow-x-hidden lg:hidden`}
            >
              {activeAction && !activeActionUnavailableMessage ? (
                <div data-planner-mobile-active-decision="true">
                  {renderPlanningForm(undefined, 'mobile')}
                </div>
              ) : null}

              <PlannerDateHeader
                viewMode={viewMode}
                variant="mobile"
                activeDateLabel={activeDateLabel}
                onPrevious={handlePreviousDate}
                onNext={handleNextDate}
                onToday={handleTodayClick}
                onViewModeChange={setViewMode}
              />

              {viewMode === 'day' ? (
                <>
                  <PlannerDayAgenda
                    selectedDayKey={planner.selectedDayKey}
                    selectedDayLabel={selectedDayLabel}
                    items={planner.selectedDayItems}
                    workloadSummary={planner.selectedDayWorkloadSummary}
                    timeGrid={planner.selectedDayTimeGrid}
                    workingHours={selectedDayWorkingHours}
                    activeSlot={activeSlot}
                    activeAction={activeAction}
                    clearState={clearState}
                    onOpenPlannerForm={openPlannerForm}
                    onTimeSlotClick={handleTimeSlotClick}
                    onRequestClearConfirm={openClearConfirm}
                    onCancelClear={() => setClearState(null)}
                    onConfirmClear={handleClearConfirm}
                    renderPlanningForm={renderPlanningForm}
                    dragSource={dragSourceConfig}
                    dropTarget={dropTargetConfig}
                    anchorViewport="mobile"
                  />
                  <PlannerPanel
                    title="Do zaplanowania"
                    items={planner.unscheduledItems}
                    emptyTitle="Brak spraw do zaplanowania."
                    emptyMessage=""
                    dateMode="preference"
                    actionScope="unscheduled"
                    activeAction={activeAction}
                    clearState={clearState}
                    onOpenPlannerForm={openPlannerForm}
                    onRequestClearConfirm={openClearConfirm}
                    onCancelClear={() => setClearState(null)}
                    onConfirmClear={handleClearConfirm}
                    renderPlanningForm={renderPlanningForm}
                    dragSource={dragSourceConfig}
                    anchorViewport="mobile"
                  />
                  {planner.clientPreferenceItems.length > 0 ? (
                    <PlannerPanel
                      title="Preferencje klienta"
                      items={planner.clientPreferenceItems}
                      emptyTitle="Brak preferencji klienta"
                      emptyMessage="Ta sekcja pokazuje tylko sugestie z pola preferowanego terminu."
                      dateMode="preference"
                      actionScope="none"
                      activeAction={activeAction}
                      clearState={clearState}
                      onOpenPlannerForm={openPlannerForm}
                      onRequestClearConfirm={openClearConfirm}
                      onCancelClear={() => setClearState(null)}
                      onConfirmClear={handleClearConfirm}
                      renderPlanningForm={renderPlanningForm}
                      dragSource={dragSourceConfig}
                      anchorViewport="mobile"
                      quiet
                    />
                  ) : null}
                </>
              ) : null}

              {viewMode === 'week' ? (
                <PlannerWeekOverview
                  variant="mobile"
                  weekItems={planner.weekItems}
                  onSelectWeekDay={handleSelectWeekDay}
                  onOpenDayDetails={handleOpenDayDetails}
                  activeAction={activeAction}
                  clearState={clearState}
                  onOpenPlannerForm={openPlannerForm}
                  onRequestClearConfirm={openClearConfirm}
                  onCancelClear={() => setClearState(null)}
                  onConfirmClear={handleClearConfirm}
                  dropTarget={dropTargetConfig}
                />
              ) : null}

              {viewMode === 'month' ? (
                <PlannerMonthOverview
                  selectedDayKey={planner.selectedDayKey}
                  scheduledCountsByDay={planner.scheduledCountsByDay}
                  clientPreferenceCountsByDay={planner.clientPreferenceCountsByDay}
                  workloadSummaryByDay={planner.workloadSummaryByDay}
                  onOpenDayDetails={handleOpenDayDetails}
                  onPreviousMonth={handlePreviousMonth}
                  onNextMonth={handleNextMonth}
                  dropTarget={dropTargetConfig}
                />
              ) : null}

              {dayDetailsDayKey && dayDetailsWorkloadSummary && dayDetailsLabel ? (
                <PlannerDayDetailsPanel
                  dayKey={dayDetailsDayKey}
                  dayLabel={dayDetailsLabel}
                  items={dayDetailsItems}
                  workloadSummary={dayDetailsWorkloadSummary}
                  activeAction={activeAction}
                  clearState={clearState}
                  onOpenPlannerForm={openPlannerForm}
                  onRequestClearConfirm={openClearConfirm}
                  onCancelClear={() => setClearState(null)}
                  onConfirmClear={handleClearConfirm}
                  renderPlanningForm={renderPlanningForm}
                  dragSource={dragSourceConfig}
                  anchorViewport="mobile"
                  onPlanOnDay={handlePlanOnDay}
                  onClose={handleCloseDayDetails}
                />
              ) : null}

              {viewMode !== 'day' ? (
                <>
                  <PlannerPanel
                    title="Do zaplanowania"
                    items={planner.unscheduledItems}
                    emptyTitle="Brak spraw do zaplanowania."
                    emptyMessage=""
                    dateMode="preference"
                    actionScope="unscheduled"
                    activeAction={activeAction}
                    clearState={clearState}
                    onOpenPlannerForm={openPlannerForm}
                    onRequestClearConfirm={openClearConfirm}
                    onCancelClear={() => setClearState(null)}
                    onConfirmClear={handleClearConfirm}
                    renderPlanningForm={renderPlanningForm}
                    dragSource={dragSourceConfig}
                    anchorViewport="mobile"
                  />
                  {planner.clientPreferenceItems.length > 0 ? (
                    <PlannerPanel
                      title="Preferencje klienta"
                      items={planner.clientPreferenceItems}
                      emptyTitle="Brak preferencji klienta"
                      emptyMessage="Ta sekcja pokazuje tylko sugestie z pola preferowanego terminu."
                      dateMode="preference"
                      actionScope="none"
                      activeAction={activeAction}
                      clearState={clearState}
                      onOpenPlannerForm={openPlannerForm}
                      onRequestClearConfirm={openClearConfirm}
                      onCancelClear={() => setClearState(null)}
                      onConfirmClear={handleClearConfirm}
                      renderPlanningForm={renderPlanningForm}
                      dragSource={dragSourceConfig}
                      anchorViewport="mobile"
                      quiet
                    />
                  ) : null}
                </>
              ) : null}
            </div>
          </>
        )}
      </div>
    </ServiceWorkspaceShell>
  );
};

const DeepLinkPlannerStateNotice: React.FC<{ state: PlannerDeepLinkState }> = ({ state }) => {
  if (!state || state.kind === 'ready') {
    return null;
  }

  const isTerminal = state.kind === 'terminal';

  return (
    <section
      data-testid="service-planner-deep-link-state"
      data-planner-surface="notice"
      className="rounded-lg bg-primary-dark-200/24 p-3 sm:p-4"
      aria-live="polite"
    >
      <p className="text-sm font-semibold text-primary-light-100">
        {isTerminal
          ? 'Tego zlecenia nie można już planować'
          : 'Nie udało się otworzyć zlecenia w planerze'}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-primary-light-300">
        {isTerminal
          ? 'Sprawa jest już w stanie końcowym, więc termin pracy serwisu pozostaje tylko historią.'
          : 'Zlecenie nie istnieje albo nie jest przypisane do Twojego serwisu.'}
      </p>
      <Link to="/app/service/planner" className={`${PLANNER_NAV_LINK_CLASS} mt-3`}>
        Wróć do planera
      </Link>
    </section>
  );
};

const PlannerDateHeader: React.FC<{
  viewMode: PlannerViewMode;
  variant: PlannerViewSegmentVariant;
  activeDateLabel: string;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  onViewModeChange: (viewMode: PlannerViewMode) => void;
}> = ({ viewMode, variant, activeDateLabel, onPrevious, onNext, onToday, onViewModeChange }) => {
  const visibleModeLabel =
    viewMode === 'week' ? 'Tydzień' : viewMode === 'month' ? 'Miesiąc' : 'Dzień';
  const titleLabel = `${visibleModeLabel} · ${activeDateLabel}`;
  const previousLabel =
    viewMode === 'week'
      ? 'Poprzedni tydzień'
      : viewMode === 'month'
        ? 'Poprzedni miesiąc'
        : 'Poprzedni dzień';
  const nextLabel =
    viewMode === 'week'
      ? 'Następny tydzień'
      : viewMode === 'month'
        ? 'Następny miesiąc'
        : 'Następny dzień';

  return (
    <section
      aria-label="Sterowanie widokiem planera"
      data-planner-controls="compact"
      data-planner-surface="controls"
      className="min-w-0 rounded-lg bg-primary-dark-200/16 px-2.5 py-2 sm:px-3 sm:py-2.5"
    >
      <div className="flex min-w-0 flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-primary-light-100 sm:text-base">
            {titleLabel}
          </h2>
        </div>

        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between xl:justify-end">
          <div className="flex min-w-0 items-center gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="neutral"
              className={PLANNER_TONAL_BUTTON_CLASS}
              onClick={onToday}
            >
              Dzisiaj
            </Button>
            <button
              type="button"
              aria-label={previousLabel}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent bg-primary-dark-200/42 text-primary-light-100 transition-colors hover:bg-primary-dark-300/48 focus-outline"
              onClick={onPrevious}
            >
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label={nextLabel}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent bg-primary-dark-200/42 text-primary-light-100 transition-colors hover:bg-primary-dark-300/48 focus-outline"
              onClick={onNext}
            >
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <PlannerViewSegment
            viewMode={viewMode}
            variant={variant}
            onViewModeChange={onViewModeChange}
          />
        </div>
      </div>
    </section>
  );
};

const PlannerViewSegment: React.FC<{
  viewMode: PlannerViewMode;
  variant: PlannerViewSegmentVariant;
  onViewModeChange: (viewMode: PlannerViewMode) => void;
}> = ({ viewMode, variant, onViewModeChange }) => {
  const options: Array<{ value: PlannerViewMode; label: string; visibleLabel: string }> = [
    { value: 'day', label: 'Dzień', visibleLabel: 'Dzień' },
    { value: 'week', label: 'Tydzień', visibleLabel: 'Tydzień' },
    ...(variant === 'mobile'
      ? ([{ value: 'month', label: 'Miesiąc', visibleLabel: 'Miesiąc' }] as Array<{
          value: PlannerViewMode;
          label: string;
          visibleLabel: string;
        }>)
      : []),
  ];

  return (
    <div
      data-testid="service-planner-view-segment"
      role="tablist"
      aria-label="Tryb widoku planera"
      className={`grid w-full min-w-0 rounded-md bg-primary-dark-100/24 p-0.5 sm:w-auto ${
        variant === 'mobile' ? 'grid-cols-3' : 'grid-cols-2'
      }`}
    >
      {options.map((option) => {
        const isActive = option.value === viewMode;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={option.label}
            className={`min-h-[32px] min-w-0 rounded-[5px] px-1.5 text-xs font-semibold transition-colors focus-outline sm:px-2 ${
              isActive
                ? 'bg-ratuj-400 text-white shadow-button'
                : 'text-primary-light-300 hover:bg-primary-dark-300/35 hover:text-primary-light-100'
            }`}
            onClick={() => onViewModeChange(option.value)}
          >
            <span className="block truncate" aria-hidden="true">
              {option.visibleLabel}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const PlannerDayAgenda: React.FC<{
  selectedDayKey: string;
  selectedDayLabel: string;
  items: ServicePlannerItem[];
  workloadSummary: ServicePlannerDayWorkloadSummary;
  timeGrid: ServicePlannerDayTimeGrid;
  workingHours: PlannerWorkingHoursForDay;
  activeSlot: ActivePlannerSlot | null;
  activeAction: ActivePlannerAction | null;
  clearState: ClearPlannerState | null;
  onOpenPlannerForm: (item: ServicePlannerItem, mode: PlannerActionMode) => void;
  onTimeSlotClick: (dayKey: string, startTime: string) => void;
  onRequestClearConfirm: (requestId: string) => void;
  onCancelClear: () => void;
  onConfirmClear: (requestId: string) => void;
  renderPlanningForm: (
    item?: ServicePlannerItem,
    anchorViewport?: PlannerDecisionViewport
  ) => React.ReactNode;
  dragSource: PlannerDragSourceConfig;
  dropTarget: PlannerDropTargetConfig;
  anchorViewport?: PlannerDecisionViewport;
}> = ({
  selectedDayKey,
  selectedDayLabel,
  items,
  workloadSummary,
  timeGrid,
  workingHours,
  activeSlot,
  activeAction,
  clearState,
  onOpenPlannerForm,
  onTimeSlotClick,
  onRequestClearConfirm,
  onCancelClear,
  onConfirmClear,
  renderPlanningForm,
  dragSource,
  dropTarget,
  anchorViewport = 'desktop',
}) => {
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [expandedMobileRequestId, setExpandedMobileRequestId] = useState<string | null>(null);
  const [extraEarlyHours, setExtraEarlyHours] = useState(0);
  const [extraLateHours, setExtraLateHours] = useState(0);
  const visibleTimeGrid = useMemo(
    () => getPlannerExtendedTimeGrid(timeGrid, extraEarlyHours, extraLateHours),
    [extraEarlyHours, extraLateHours, timeGrid]
  );
  const selectedActiveSlot = activeSlot?.dayKey === selectedDayKey ? activeSlot : null;
  const gridHeight = Math.max(1, visibleTimeGrid.hours.length) * SERVICE_PLANNER_HOUR_ROW_HEIGHT_PX;
  const timeGridSlots = useMemo(() => getPlannerTimeGridSlots(visibleTimeGrid), [visibleTimeGrid]);
  const workingHoursBandStyle = useMemo(
    () => getPlannerWorkingHoursBandStyle(visibleTimeGrid, workingHours),
    [visibleTimeGrid, workingHours]
  );
  const showsCompactDropConfirmation =
    anchorViewport === 'desktop' &&
    activeAction?.source === 'calendarDrop' &&
    activeAction.mode === 'reschedule' &&
    Boolean(activeAction.prefilledStartTime);

  useEffect(() => {
    if (!expandedRequestId || items.some((item) => item.requestId === expandedRequestId)) {
      return;
    }
    setExpandedRequestId(null);
  }, [expandedRequestId, items]);

  useEffect(() => {
    if (
      !expandedMobileRequestId ||
      items.some((item) => item.requestId === expandedMobileRequestId)
    ) {
      return;
    }
    setExpandedMobileRequestId(null);
  }, [expandedMobileRequestId, items]);

  useEffect(() => {
    setExtraEarlyHours(0);
    setExtraLateHours(0);
  }, [selectedDayKey]);

  useEffect(() => {
    if (!expandedRequestId && !expandedMobileRequestId) {
      return undefined;
    }

    const handleOutsidePointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-planner-action-surface-root="true"]')) {
        return;
      }
      setExpandedRequestId(null);
      setExpandedMobileRequestId(null);
    };

    document.addEventListener('mousedown', handleOutsidePointer);
    document.addEventListener('touchstart', handleOutsidePointer);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointer);
      document.removeEventListener('touchstart', handleOutsidePointer);
    };
  }, [expandedMobileRequestId, expandedRequestId]);

  return (
    <section
      aria-label="Agenda dnia"
      data-planner-surface="section"
      className={PLANNER_PANEL_SURFACE_CLASS}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setExpandedRequestId(null);
          setExpandedMobileRequestId(null);
        }
      }}
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-primary-light-100 sm:text-base">
            Agenda dnia
          </h2>
          <p className="mt-0.5 truncate text-xs text-primary-light-400">{selectedDayLabel}</p>
          <p
            data-testid="service-planner-day-workload-summary"
            className="mt-1 truncate text-xs font-medium text-primary-light-300"
          >
            {formatPlannerWorkloadSummary(workloadSummary)}
          </p>
        </div>
        {workloadSummary.loadLevel === 'heavy' ? (
          <span className="shrink-0 rounded-md bg-status-warning-500/10 px-2 py-1 text-[11px] font-semibold text-status-warning-300">
            Dużo pracy
          </span>
        ) : null}
      </div>

      <div className="mt-3 hidden min-w-0 items-center justify-between gap-3 rounded-lg bg-primary-dark-100/18 px-3 py-2 md:flex">
        <p
          data-testid="service-planner-working-hours-label"
          data-working-hours-source={workingHours.source}
          className="min-w-0 truncate text-xs font-medium text-primary-light-300"
        >
          {workingHours.label}
        </p>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            variant="neutral"
            className={PLANNER_TONAL_BUTTON_CLASS}
            disabled={visibleTimeGrid.rangeStartHour <= 0}
            onClick={() =>
              setExtraEarlyHours((value) => Math.min(timeGrid.rangeStartHour, value + 2))
            }
          >
            Wcześniej
          </Button>
          <Button
            type="button"
            size="sm"
            variant="neutral"
            className={PLANNER_TONAL_BUTTON_CLASS}
            disabled={visibleTimeGrid.rangeEndHour >= 24}
            onClick={() =>
              setExtraLateHours((value) => Math.min(24 - timeGrid.rangeEndHour, value + 2))
            }
          >
            Później
          </Button>
        </div>
      </div>

      {selectedActiveSlot ? (
        <div
          data-testid="service-planner-active-slot"
          className="mt-3 rounded-lg border-l-2 border-l-ratuj-300/65 bg-ratuj-300/10 px-3 py-2"
        >
          <p className="text-sm font-semibold text-primary-light-100">
            Aktywny slot: {selectedActiveSlot.startTime}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-primary-light-300">
            Wybierz zlecenie z listy do zaplanowania.
          </p>
        </div>
      ) : null}

      <div
        data-testid="service-planner-day-time-grid"
        data-planner-surface="grid"
        data-range-start-hour={visibleTimeGrid.rangeStartHour}
        data-range-end-hour={visibleTimeGrid.rangeEndHour}
        className="mt-3 hidden min-w-0 grid-cols-[3.5rem_minmax(0,1fr)] gap-3 md:grid"
      >
        <div className="relative" style={{ height: `${gridHeight}px` }} aria-hidden="true">
          {visibleTimeGrid.hours.map((hour) => (
            <div
              key={`label-${hour}`}
              className="flex justify-end pr-1 text-[11px] font-semibold tabular-nums text-primary-light-400"
              style={{ height: `${SERVICE_PLANNER_HOUR_ROW_HEIGHT_PX}px` }}
            >
              {formatPlannerHourLabel(hour)}
            </div>
          ))}
        </div>

        <div className={PLANNER_GRID_SURFACE_CLASS} style={{ height: `${gridHeight}px` }}>
          {workingHoursBandStyle ? (
            <div
              data-testid="service-planner-working-hours-band"
              data-working-hours-source={workingHours.source}
              className="rs-planner-working-hours-band"
              style={workingHoursBandStyle}
              aria-hidden="true"
            />
          ) : null}

          <div className="absolute inset-0 z-0">
            {timeGridSlots.map((slot) => {
              const startTime = slot.startTime;
              const slotKey = getPlannerDropSlotKey(selectedDayKey, startTime);
              const isSlotDropEnabled = dropTarget.isSlotEnabled;
              const isDropActive = isSlotDropEnabled && dropTarget.activeSlotKey === slotKey;
              const isActiveSlot =
                selectedActiveSlot?.dayKey === selectedDayKey &&
                selectedActiveSlot.startTime === startTime;

              return (
                <button
                  key={`slot-${slot.minutes}`}
                  type="button"
                  data-testid={`service-planner-time-slot-${selectedDayKey}-${startTime}`}
                  data-planner-drop-target={isSlotDropEnabled ? 'hour' : undefined}
                  data-drop-active={isDropActive ? 'true' : undefined}
                  aria-label={`Slot ${startTime}`}
                  className={`relative block w-full border-t px-3 text-left transition-colors focus-outline ${
                    isDropActive
                      ? 'border-ratuj-300/45 bg-ratuj-300/16 shadow-[inset_3px_0_0_rgba(73,177,92,0.55)]'
                      : isActiveSlot
                        ? 'border-ratuj-300/28 bg-ratuj-300/10'
                        : 'border-primary-dark-300/18 hover:bg-primary-dark-300/18'
                  }`}
                  style={{
                    height: `${(SERVICE_PLANNER_HOUR_ROW_HEIGHT_PX * TIME_GRID_STEP_MINUTES) / 60}px`,
                  }}
                  onClick={() => onTimeSlotClick(selectedDayKey, startTime)}
                  onDragEnter={
                    isSlotDropEnabled
                      ? (event) => dropTarget.onDragOver(event, selectedDayKey, startTime)
                      : undefined
                  }
                  onDragOver={
                    isSlotDropEnabled
                      ? (event) => dropTarget.onDragOver(event, selectedDayKey, startTime)
                      : undefined
                  }
                  onDragLeave={
                    isSlotDropEnabled
                      ? (event) => dropTarget.onDragLeave(event, selectedDayKey, startTime)
                      : undefined
                  }
                  onDrop={
                    isSlotDropEnabled
                      ? (event) => dropTarget.onDrop(event, selectedDayKey, startTime)
                      : undefined
                  }
                >
                  <span className="sr-only">Ustaw slot {startTime}</span>
                  {isDropActive ? (
                    <span className="pointer-events-none absolute left-3 top-2 inline-flex rounded-md bg-primary-dark-200/92 px-2 py-1 text-[11px] font-semibold text-ratuj-200">
                      Puść, aby wybrać {startTime}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {timeGrid.blocks.length === 0 ? (
            <div
              data-testid="service-planner-grid-empty-copy"
              data-planner-empty-copy="pointer-pass-through"
              className={PLANNER_GRID_EMPTY_COPY_CLASS}
            >
              <p className="text-sm font-medium text-primary-light-100">Brak pracy w tym dniu.</p>
              <p className="mt-1 text-sm leading-relaxed text-primary-light-300">
                Puść zlecenie na wybranej godzinie.
              </p>
            </div>
          ) : null}

          <div className="pointer-events-none absolute inset-0 z-20">
            {visibleTimeGrid.blocks.map((block, index) => {
              const isExpanded =
                expandedRequestId === block.item.requestId ||
                activeAction?.requestId === block.item.requestId ||
                clearState?.requestId === block.item.requestId;
              const conflictOffset = block.hasConflict ? (index % 3) * 9 : 0;

              return (
                <div
                  key={`time-block-${block.item.requestId}`}
                  data-event-layer="above-grid"
                  className="absolute pointer-events-auto z-20"
                  style={{
                    ...getPlannerTimeBlockStyle(block),
                    left: `${8 + conflictOffset}px`,
                    right: '8px',
                    zIndex: 10 + index,
                  }}
                >
                  <PlannerTimeGridBlockCard
                    block={block}
                    isExpanded={isExpanded}
                    activeAction={activeAction}
                    clearState={clearState}
                    onToggle={() =>
                      setExpandedRequestId((current) =>
                        current === block.item.requestId ? null : block.item.requestId
                      )
                    }
                    onOpenPlannerForm={onOpenPlannerForm}
                    onRequestClearConfirm={onRequestClearConfirm}
                    onCancelClear={onCancelClear}
                    onConfirmClear={onConfirmClear}
                    dragSource={dragSource}
                    anchorViewport={anchorViewport}
                  />
                </div>
              );
            })}
          </div>

          {showsCompactDropConfirmation ? (
            <div
              data-testid="service-planner-drop-confirmation-popover"
              data-planner-drop-confirmation="popover"
              className="rs-planner-drop-popover"
            >
              {renderPlanningForm(undefined, 'desktop')}
            </div>
          ) : null}
        </div>
      </div>

      {anchorViewport === 'desktop' && activeAction && !showsCompactDropConfirmation ? (
        <div
          data-testid="service-planner-inline-decision"
          data-planner-inline-decision="calendar"
          className="mt-3 hidden md:block"
        >
          {renderPlanningForm(undefined, 'desktop')}
        </div>
      ) : null}

      <div className="mt-3 md:hidden">
        {items.length === 0 ? (
          <div className="rounded-lg bg-primary-dark-100/16 px-3 py-3">
            <p className="text-sm font-medium text-primary-light-100">Brak pracy w tym dniu.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {items.map((item) => (
              <PlannerItemRow
                key={`mobile-day-${item.requestId}`}
                item={item}
                dateMode="scheduleTime"
                actionScope="scheduled"
                activeAction={activeAction}
                clearState={clearState}
                onOpenPlannerForm={onOpenPlannerForm}
                onRequestClearConfirm={onRequestClearConfirm}
                onCancelClear={onCancelClear}
                onConfirmClear={onConfirmClear}
                renderPlanningForm={renderPlanningForm}
                dragSource={dragSource}
                anchorViewport={anchorViewport}
                showInlineForm={false}
                collapsibleActions
                isActionExpanded={expandedMobileRequestId === item.requestId}
                onToggleActions={() =>
                  setExpandedMobileRequestId((current) =>
                    current === item.requestId ? null : item.requestId
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const PlannerTimeGridBlockCard: React.FC<{
  block: ServicePlannerTimeGridBlock;
  isExpanded: boolean;
  activeAction: ActivePlannerAction | null;
  clearState: ClearPlannerState | null;
  onToggle: () => void;
  onOpenPlannerForm: (item: ServicePlannerItem, mode: PlannerActionMode) => void;
  onRequestClearConfirm: (requestId: string) => void;
  onCancelClear: () => void;
  onConfirmClear: (requestId: string) => void;
  dragSource: PlannerDragSourceConfig;
  anchorViewport: PlannerDecisionViewport;
}> = ({
  block,
  isExpanded,
  activeAction,
  clearState,
  onToggle,
  onOpenPlannerForm,
  onRequestClearConfirm,
  onCancelClear,
  onConfirmClear,
  dragSource,
  anchorViewport,
}) => {
  const item = block.item;
  const request = item.request;
  const isActiveItem = activeAction?.requestId === item.requestId;
  const canDrag = dragSource.isEnabled;
  const isDragging = dragSource.draggingRequestId === item.requestId;
  const startLabel = formatPlannerTimeFromMinutes(block.startMinutes);
  const endLabel =
    block.endMinutes !== null ? formatPlannerTimeFromMinutes(block.endMinutes) : null;
  const readableParts = getPlannerReadableEventParts(item);
  const readableEventLine = buildPlannerReadableEventLine({
    ...readableParts,
    time: endLabel ? `${startLabel} - ${endLabel}` : startLabel,
  });
  const readableMobileLead = buildPlannerReadableEventMobileLead({
    ...readableParts,
    time: startLabel,
  });
  const readableMobileDetail = buildPlannerReadableEventMobileDetail(readableParts);

  return (
    <article
      data-testid={`service-planner-item-${item.requestId}`}
      data-time-grid-block="true"
      data-time-grid-block-id={item.requestId}
      data-grid-event-surface="true"
      data-planner-surface="grid-event"
      data-planner-action-surface-root="true"
      draggable={canDrag ? true : undefined}
      data-draggable={canDrag ? 'true' : undefined}
      data-dragging={isDragging ? 'true' : undefined}
      className={`rs-planner-event-card rs-planner-event-card--grid relative z-20 flex h-full flex-col overflow-hidden px-3 py-2 ${
        isDragging
          ? 'rs-planner-event-card--dragging'
          : block.hasConflict
            ? 'rs-planner-event-card--conflict'
            : isActiveItem
              ? 'rs-planner-event-card--active'
              : 'rs-planner-event-card--idle'
      }`}
      onDragStart={canDrag ? (event) => dragSource.onDragStart(event, item) : undefined}
      onDragEnd={canDrag ? dragSource.onDragEnd : undefined}
    >
      <button
        type="button"
        className="min-w-0 text-left focus-outline"
        aria-expanded={isExpanded}
        onClick={onToggle}
      >
        <span className="flex min-w-0 items-start justify-between gap-2">
          <span className="min-w-0">
            <span className="hidden line-clamp-2 break-words text-sm font-semibold leading-snug text-primary-light-100 sm:block">
              {readableEventLine}
            </span>
            <span className="block truncate text-sm font-semibold text-primary-light-100 sm:hidden">
              {readableMobileLead}
            </span>
            <span className="mt-0.5 block truncate text-[11px] text-primary-light-400">
              {readableMobileDetail}
            </span>
            <span className="mt-0.5 block truncate text-[11px] text-primary-light-400">
              {readableParts.status}
            </span>
          </span>
          <span className="mt-0.5 flex shrink-0 items-center gap-1 text-primary-light-400">
            {canDrag ? <Bars3Icon className="hidden h-4 w-4 md:block" aria-hidden="true" /> : null}
            <ClockIcon className="h-4 w-4" aria-hidden="true" />
          </span>
        </span>
        {block.hasConflict ? (
          <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-status-error-300">
            <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Kolizja terminu
          </span>
        ) : null}
      </button>

      {isExpanded ? (
        <div className="mt-2 min-w-0 pt-2">
          <p className="truncate text-[11px] text-primary-light-400">
            {getRepairRequestOperationalIndexLabel(request)}
          </p>
          <div
            data-planner-action-row="inline"
            className="mt-2 flex min-w-0 flex-wrap items-center gap-2"
          >
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className={PLANNER_TONAL_BUTTON_CLASS}
              icon={<PencilSquareIcon className="h-4 w-4" aria-hidden="true" />}
              onClick={() => onOpenPlannerForm(item, 'reschedule')}
              disabled={
                activeAction?.requestId === item.requestId && activeAction.mode === 'reschedule'
              }
            >
              Przełóż
            </Button>
            <Button
              type="button"
              size="sm"
              variant="neutral"
              icon={<TrashIcon className="h-4 w-4" aria-hidden="true" />}
              className={PLANNER_DESTRUCTIVE_TONAL_BUTTON_CLASS}
              onClick={() => onRequestClearConfirm(item.requestId)}
              disabled={clearState?.requestId === item.requestId && clearState.isSubmitting}
            >
              Usuń z planu
            </Button>
            <Link to={buildPlannerDetailHref(item.requestId)} className={PLANNER_NAV_LINK_CLASS}>
              <span>Otwórz</span>
              <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          {clearState?.requestId === item.requestId ? (
            <ClearPlanConfirm
              requestId={item.requestId}
              anchorViewport={anchorViewport}
              isSubmitting={clearState.isSubmitting}
              error={clearState.error}
              onCancel={onCancelClear}
              onConfirm={() => onConfirmClear(item.requestId)}
            />
          ) : null}
        </div>
      ) : null}
    </article>
  );
};

const PlannerWeekOverview: React.FC<{
  variant: PlannerViewSegmentVariant;
  weekItems: ServicePlannerWeekDay[];
  onSelectWeekDay: (dayKey: string) => void;
  onOpenDayDetails: (dayKey: string) => void;
  activeAction: ActivePlannerAction | null;
  clearState: ClearPlannerState | null;
  onOpenPlannerForm: (item: ServicePlannerItem, mode: PlannerActionMode) => void;
  onRequestClearConfirm: (requestId: string) => void;
  onCancelClear: () => void;
  onConfirmClear: (requestId: string) => void;
  dropTarget: PlannerDropTargetConfig;
}> = ({
  variant,
  weekItems,
  onSelectWeekDay,
  onOpenDayDetails,
  activeAction,
  clearState,
  onOpenPlannerForm,
  onRequestClearConfirm,
  onCancelClear,
  onConfirmClear,
  dropTarget,
}) => {
  const [expandedWeekRequestId, setExpandedWeekRequestId] = useState<string | null>(null);
  const isMobileVariant = variant === 'mobile';
  const weekScheduledCount = weekItems.reduce((sum, day) => sum + day.items.length, 0);

  useEffect(() => {
    if (!expandedWeekRequestId) {
      return;
    }
    const stillVisible = weekItems.some((day) =>
      day.items.some((item) => item.requestId === expandedWeekRequestId)
    );
    if (!stillVisible) {
      setExpandedWeekRequestId(null);
    }
  }, [expandedWeekRequestId, weekItems]);

  useEffect(() => {
    if (!expandedWeekRequestId) {
      return undefined;
    }

    const handleOutsidePointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-planner-action-surface-root="true"]')) {
        return;
      }
      setExpandedWeekRequestId(null);
    };

    document.addEventListener('mousedown', handleOutsidePointer);
    document.addEventListener('touchstart', handleOutsidePointer);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointer);
      document.removeEventListener('touchstart', handleOutsidePointer);
    };
  }, [expandedWeekRequestId]);

  return (
    <section
      data-planner-surface="week"
      className="rs-planner-week-overview"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setExpandedWeekRequestId(null);
        }
      }}
    >
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-primary-light-100 sm:text-base">
            Agenda tygodnia
          </h2>
        </div>
        <Badge variant="neutral" size="sm" className="shrink-0 tabular-nums">
          {weekScheduledCount}
        </Badge>
      </div>

      <div
        data-testid="service-planner-week-calendar"
        data-mobile-compact-week={isMobileVariant ? 'true' : undefined}
        data-week-layout={isMobileVariant ? 'workload-overview' : 'mini-card-flow'}
        className="mt-3 flex min-w-0 flex-col gap-2"
      >
        {weekItems.map((day) => {
          const isDropActive = dropTarget.isDayEnabled && dropTarget.activeDayKey === day.dayKey;
          const hasItems = day.items.length > 0;
          const isHeavyDay = day.loadLevel === 'heavy';
          const visibleItems = day.items.slice(0, isMobileVariant ? 2 : day.items.length);
          const overflowCount = Math.max(0, day.items.length - visibleItems.length);
          const workloadClass = isHeavyDay
            ? 'rs-planner-week-day--heavy'
            : hasItems
              ? 'rs-planner-week-day--work'
              : 'rs-planner-week-day--empty';

          if (isMobileVariant) {
            return (
              <button
                key={day.dayKey}
                type="button"
                data-testid={`service-planner-week-day-${day.dayKey}`}
                data-mobile-week-row="compact"
                data-load-level={day.loadLevel}
                data-planner-drop-target={dropTarget.isDayEnabled ? 'day' : undefined}
                data-drop-active={isDropActive ? 'true' : undefined}
                className={`rs-planner-week-day min-w-0 text-left transition-colors focus-outline ${
                  hasItems ? 'py-3' : 'py-2'
                } ${
                  isDropActive ? 'rs-planner-week-day--drop' : workloadClass
                } ${day.isSelected ? 'rs-planner-week-day--selected' : ''}`}
                onClick={() => onOpenDayDetails(day.dayKey)}
                onDragEnter={
                  dropTarget.isDayEnabled
                    ? (event) => dropTarget.onDragOver(event, day.dayKey)
                    : undefined
                }
                onDragOver={
                  dropTarget.isDayEnabled
                    ? (event) => dropTarget.onDragOver(event, day.dayKey)
                    : undefined
                }
                onDragLeave={
                  dropTarget.isDayEnabled
                    ? (event) => dropTarget.onDragLeave(event, day.dayKey)
                    : undefined
                }
                onDrop={
                  dropTarget.isDayEnabled
                    ? (event) => dropTarget.onDrop(event, day.dayKey)
                    : undefined
                }
              >
                <span className="flex min-w-0 items-center justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block text-[11px] font-semibold uppercase tracking-normal text-primary-light-400">
                      {formatPlannerWeekdayLabel(day.dayKey)}
                    </span>
                    <span className="mt-0.5 block text-sm font-semibold text-primary-light-100">
                      {formatPlannerShortDayLabel(day.dayKey)}
                    </span>
                    <span
                      data-testid={`service-planner-week-day-load-${day.dayKey}`}
                      className="mt-1 block truncate text-[11px] font-medium text-primary-light-400"
                    >
                      {hasItems ? formatPlannerWorkloadSummary(day.workloadSummary) : 'Bez pracy'}
                    </span>
                  </span>
                  <span className="inline-flex shrink-0 items-center gap-1.5">
                    {isHeavyDay ? (
                      <span className="rounded-md bg-status-warning-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-status-warning-300">
                        Dużo pracy
                      </span>
                    ) : null}
                    {day.conflictCount > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-status-error-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-status-error-300">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        {day.conflictCount}
                      </span>
                    ) : null}
                    <span
                      className={`rounded-md px-2 py-0.5 text-base font-semibold tabular-nums ${
                        hasItems
                          ? 'bg-primary-dark-200/46 text-primary-light-100'
                          : 'bg-primary-dark-200/26 text-primary-light-400'
                      }`}
                    >
                      {day.scheduledCount}
                    </span>
                  </span>
                </span>
                {visibleItems.length > 0 ? (
                  <span className="mt-2 flex min-w-0 flex-col gap-1.5">
                    {visibleItems.map((item) => {
                      const readableParts = getPlannerReadableEventParts(item);

                      return (
                        <span
                          key={item.requestId}
                          data-planner-week-event-preview="true"
                          data-mobile-week-entry="true"
                          className="rs-planner-week-event-preview"
                        >
                          <span className="min-w-0 truncate">
                            {buildPlannerReadableEventMobileLead(readableParts)}
                          </span>
                          <span className="min-w-0 truncate text-primary-light-400">
                            {buildPlannerReadableEventMobileDetail(readableParts)}
                          </span>
                        </span>
                      );
                    })}
                    {overflowCount > 0 ? (
                      <span className="rs-planner-week-overflow">+{overflowCount} więcej</span>
                    ) : null}
                  </span>
                ) : null}
              </button>
            );
          }

          return (
            <article
              key={day.dayKey}
              data-testid={`service-planner-week-day-${day.dayKey}`}
              data-desktop-week-day="mini-card-flow"
              data-load-level={day.loadLevel}
              data-planner-drop-target={dropTarget.isDayEnabled ? 'day' : undefined}
              data-drop-active={isDropActive ? 'true' : undefined}
              className={`rs-planner-week-day min-w-0 transition-colors ${
                hasItems ? 'py-3' : 'py-2'
              } ${
                isDropActive ? 'rs-planner-week-day--drop' : workloadClass
              } ${day.isSelected ? 'rs-planner-week-day--selected' : ''}`}
              onDragEnter={
                dropTarget.isDayEnabled
                  ? (event) => dropTarget.onDragOver(event, day.dayKey)
                  : undefined
              }
              onDragOver={
                dropTarget.isDayEnabled
                  ? (event) => dropTarget.onDragOver(event, day.dayKey)
                  : undefined
              }
              onDragLeave={
                dropTarget.isDayEnabled
                  ? (event) => dropTarget.onDragLeave(event, day.dayKey)
                  : undefined
              }
              onDrop={
                dropTarget.isDayEnabled
                  ? (event) => dropTarget.onDrop(event, day.dayKey)
                  : undefined
              }
            >
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  className="min-w-0 text-left focus-outline"
                  onClick={() => onSelectWeekDay(day.dayKey)}
                >
                  <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-xs font-semibold uppercase tracking-normal text-primary-light-400">
                      {formatPlannerWeekdayLabel(day.dayKey)}
                    </span>
                    <span className="text-sm font-semibold text-primary-light-100">
                      {formatPlannerShortDayLabel(day.dayKey)}
                    </span>
                    <span
                      data-testid={`service-planner-week-day-load-${day.dayKey}`}
                      className="truncate text-xs font-medium text-primary-light-400"
                    >
                      {hasItems ? formatPlannerWorkloadSummary(day.workloadSummary) : 'Bez pracy'}
                    </span>
                  </span>
                </button>
                <span className="inline-flex shrink-0 items-center gap-1.5">
                  {isHeavyDay ? (
                    <span className="rounded-md bg-status-warning-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-status-warning-300">
                      Dużo pracy
                    </span>
                  ) : null}
                  {day.conflictCount > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-status-error-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-status-error-300">
                      <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {day.conflictCount}
                    </span>
                  ) : null}
                  <span
                    className={`rounded-md px-2 py-0.5 text-sm font-semibold tabular-nums ${
                      hasItems
                        ? 'bg-primary-dark-200/46 text-primary-light-100'
                        : 'bg-primary-dark-200/26 text-primary-light-400'
                    }`}
                  >
                    {day.scheduledCount}
                  </span>
                </span>
              </div>

              {hasItems ? (
                <div className="rs-planner-week-event-flow mt-2">
                  {visibleItems.map((item) => (
                    <PlannerWeekMiniCard
                      key={`week-mini-${day.dayKey}-${item.requestId}`}
                      item={item}
                      isExpanded={
                        expandedWeekRequestId === item.requestId ||
                        activeAction?.requestId === item.requestId ||
                        clearState?.requestId === item.requestId
                      }
                      activeAction={activeAction}
                      clearState={clearState}
                      onToggle={() =>
                        setExpandedWeekRequestId((current) =>
                          current === item.requestId ? null : item.requestId
                        )
                      }
                      onOpenPlannerForm={onOpenPlannerForm}
                      onRequestClearConfirm={onRequestClearConfirm}
                      onCancelClear={onCancelClear}
                      onConfirmClear={onConfirmClear}
                    />
                  ))}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
};

const PlannerWeekMiniCard: React.FC<{
  item: ServicePlannerItem;
  isExpanded: boolean;
  activeAction: ActivePlannerAction | null;
  clearState: ClearPlannerState | null;
  onToggle: () => void;
  onOpenPlannerForm: (item: ServicePlannerItem, mode: PlannerActionMode) => void;
  onRequestClearConfirm: (requestId: string) => void;
  onCancelClear: () => void;
  onConfirmClear: (requestId: string) => void;
}> = ({
  item,
  isExpanded,
  activeAction,
  clearState,
  onToggle,
  onOpenPlannerForm,
  onRequestClearConfirm,
  onCancelClear,
  onConfirmClear,
}) => {
  const readableParts = getPlannerReadableEventParts(item);
  const fullLabel = buildPlannerReadableEventLine(readableParts);
  const isActiveItem = activeAction?.requestId === item.requestId;

  return (
    <article
      data-testid={`service-planner-week-mini-card-${item.requestId}`}
      data-planner-action-surface-root="true"
      data-planner-week-mini-card="true"
      className={`rs-planner-week-mini-card ${isActiveItem ? 'rs-planner-event-card--active' : ''}`}
      title={fullLabel}
    >
      <button
        type="button"
        className="block min-w-0 text-left focus-outline"
        aria-expanded={isExpanded}
        aria-label={`Pokaż akcje: ${fullLabel}`}
        onClick={onToggle}
      >
        <span className="block truncate text-xs font-semibold text-primary-light-100">
          {readableParts.time} · {readableParts.subject}
        </span>
        <span className="mt-0.5 block truncate text-[11px] text-primary-light-400">
          {readableParts.model} · {readableParts.client}
        </span>
      </button>

      {isExpanded ? (
        <div
          data-planner-action-row="week-mini"
          className="mt-2 flex flex-wrap items-center gap-1.5"
        >
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className={PLANNER_TONAL_BUTTON_CLASS}
            icon={<PencilSquareIcon className="h-4 w-4" aria-hidden="true" />}
            onClick={() => onOpenPlannerForm(item, 'reschedule')}
            disabled={
              activeAction?.requestId === item.requestId && activeAction.mode === 'reschedule'
            }
          >
            Przełóż
          </Button>
          <Button
            type="button"
            size="sm"
            variant="neutral"
            icon={<TrashIcon className="h-4 w-4" aria-hidden="true" />}
            className={PLANNER_DESTRUCTIVE_TONAL_BUTTON_CLASS}
            onClick={() => onRequestClearConfirm(item.requestId)}
            disabled={clearState?.requestId === item.requestId && clearState.isSubmitting}
          >
            Usuń z planu
          </Button>
          <Link to={buildPlannerDetailHref(item.requestId)} className={PLANNER_NAV_LINK_CLASS}>
            <span>Otwórz</span>
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      ) : null}

      {clearState?.requestId === item.requestId ? (
        <ClearPlanConfirm
          requestId={item.requestId}
          anchorViewport="desktop"
          isSubmitting={clearState.isSubmitting}
          error={clearState.error}
          onCancel={onCancelClear}
          onConfirm={() => onConfirmClear(item.requestId)}
        />
      ) : null}
    </article>
  );
};

const PlannerMonthOverview: React.FC<{
  selectedDayKey: string;
  scheduledCountsByDay: Record<string, number>;
  clientPreferenceCountsByDay: Record<string, number>;
  workloadSummaryByDay: Record<string, ServicePlannerDayWorkloadSummary>;
  onOpenDayDetails: (dayKey: string) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  dropTarget: PlannerDropTargetConfig;
}> = ({
  selectedDayKey,
  scheduledCountsByDay,
  clientPreferenceCountsByDay,
  workloadSummaryByDay,
  onOpenDayDetails,
  onPreviousMonth,
  onNextMonth,
  dropTarget,
}) => (
  <div data-testid="service-planner-month-calendar" className="min-w-0">
    <PlannerMiniMonth
      selectedDayKey={selectedDayKey}
      scheduledCountsByDay={scheduledCountsByDay}
      clientPreferenceCountsByDay={clientPreferenceCountsByDay}
      workloadSummaryByDay={workloadSummaryByDay}
      onSelectDay={onOpenDayDetails}
      onPreviousMonth={onPreviousMonth}
      onNextMonth={onNextMonth}
      dropTarget={dropTarget}
      ariaLabel="Miesiąc"
    />
  </div>
);

const PlannerDayDetailsPanel: React.FC<{
  dayKey: string;
  dayLabel: string;
  items: ServicePlannerItem[];
  workloadSummary: ServicePlannerDayWorkloadSummary;
  activeAction: ActivePlannerAction | null;
  clearState: ClearPlannerState | null;
  onOpenPlannerForm: (item: ServicePlannerItem, mode: PlannerActionMode) => void;
  onRequestClearConfirm: (requestId: string) => void;
  onCancelClear: () => void;
  onConfirmClear: (requestId: string) => void;
  renderPlanningForm: (
    item?: ServicePlannerItem,
    anchorViewport?: PlannerDecisionViewport
  ) => React.ReactNode;
  dragSource: PlannerDragSourceConfig;
  anchorViewport: PlannerDecisionViewport;
  onPlanOnDay: (dayKey: string) => void;
  onClose?: () => void;
}> = ({
  dayKey,
  dayLabel,
  items,
  workloadSummary,
  activeAction,
  clearState,
  onOpenPlannerForm,
  onRequestClearConfirm,
  onCancelClear,
  onConfirmClear,
  renderPlanningForm,
  dragSource,
  anchorViewport,
  onPlanOnDay,
  onClose,
}) => {
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);

  useEffect(() => {
    if (!expandedRequestId || items.some((item) => item.requestId === expandedRequestId)) {
      return;
    }
    setExpandedRequestId(null);
  }, [expandedRequestId, items]);

  return (
    <section
      aria-label={`Szczegóły dnia ${dayLabel}`}
      data-testid="service-planner-day-details"
      data-planner-surface="decision"
      className="min-w-0 rounded-lg border-l-2 border-l-ratuj-300/55 bg-primary-dark-200/22 p-3 sm:p-4"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setExpandedRequestId(null);
        }
      }}
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-primary-light-100 sm:text-base">
            Szczegóły dnia
          </h2>
          <p className="mt-0.5 truncate text-xs text-primary-light-400">{dayLabel}</p>
          <p className="mt-1 truncate text-xs font-medium text-primary-light-300">
            {formatPlannerWorkloadSummary(workloadSummary)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {workloadSummary.loadLevel === 'heavy' ? (
            <span className="rounded-md bg-status-warning-500/10 px-2 py-1 text-[11px] font-semibold text-status-warning-300">
              Dużo pracy
            </span>
          ) : null}
          {onClose ? (
            <Button
              type="button"
              size="sm"
              variant="neutral"
              className={PLANNER_TONAL_BUTTON_CLASS}
              onClick={onClose}
            >
              Zamknij
            </Button>
          ) : null}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-3 rounded-lg bg-primary-dark-100/24 px-3 py-4">
          <p className="text-sm font-medium text-primary-light-100">Brak pracy w tym dniu.</p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className={`${PLANNER_TONAL_BUTTON_CLASS} mt-3`}
            onClick={() => onPlanOnDay(dayKey)}
          >
            Zaplanuj na ten dzień
          </Button>
        </div>
      ) : (
        <div className="mt-3 space-y-2.5">
          {items.map((item) => (
            <PlannerItemRow
              key={`day-details-${dayKey}-${item.requestId}`}
              item={item}
              dateMode="scheduleTime"
              actionScope="scheduled"
              activeAction={activeAction}
              clearState={clearState}
              onOpenPlannerForm={onOpenPlannerForm}
              onRequestClearConfirm={onRequestClearConfirm}
              onCancelClear={onCancelClear}
              onConfirmClear={onConfirmClear}
              renderPlanningForm={renderPlanningForm}
              dragSource={dragSource}
              anchorViewport={anchorViewport}
              showInlineForm={false}
              collapsibleActions
              isActionExpanded={expandedRequestId === item.requestId}
              onToggleActions={() =>
                setExpandedRequestId((current) =>
                  current === item.requestId ? null : item.requestId
                )
              }
            />
          ))}
        </div>
      )}
    </section>
  );
};

const PlannerSideContext: React.FC<{
  selectedDayKey: string;
  selectedDayLabel: string;
  selectedDayItems: ServicePlannerItem[];
  selectedDayWorkloadSummary: ServicePlannerDayWorkloadSummary;
  weekItems: ServicePlannerWeekDay[];
  scheduledCountsByDay: Record<string, number>;
  clientPreferenceCountsByDay: Record<string, number>;
  workloadSummaryByDay: Record<string, ServicePlannerDayWorkloadSummary>;
  onSelectDay: (dayKey: string) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  dropTarget: PlannerDropTargetConfig;
}> = ({
  selectedDayKey,
  selectedDayLabel,
  selectedDayItems,
  selectedDayWorkloadSummary,
  weekItems,
  scheduledCountsByDay,
  clientPreferenceCountsByDay,
  workloadSummaryByDay,
  onSelectDay,
  onPreviousMonth,
  onNextMonth,
  dropTarget,
}) => {
  const weekScheduledCount = weekItems.reduce((sum, day) => sum + day.scheduledCount, 0);

  return (
    <div className="min-w-0 space-y-3">
      <PlannerMiniMonth
        selectedDayKey={selectedDayKey}
        scheduledCountsByDay={scheduledCountsByDay}
        clientPreferenceCountsByDay={clientPreferenceCountsByDay}
        workloadSummaryByDay={workloadSummaryByDay}
        onSelectDay={onSelectDay}
        onPreviousMonth={onPreviousMonth}
        onNextMonth={onNextMonth}
        dropTarget={dropTarget}
      />

      <section className="rounded-lg bg-primary-dark-200/16 p-3">
        <p className="text-xs font-semibold uppercase tracking-normal text-primary-light-400">
          Podsumowanie
        </p>
        <p className="mt-1 text-sm font-semibold text-primary-light-100">{selectedDayLabel}</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-primary-dark-100/24 p-2">
            <p className="text-[11px] text-primary-light-400">Dzień</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-primary-light-100">
              {selectedDayItems.length}
            </p>
            {selectedDayWorkloadSummary.loadLevel === 'heavy' ? (
              <p className="mt-1 text-[11px] font-semibold text-status-warning-300">Dużo pracy</p>
            ) : null}
          </div>
          <div className="rounded-lg bg-primary-dark-100/24 p-2">
            <p className="text-[11px] text-primary-light-400">Tydzień</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-primary-light-100">
              {weekScheduledCount}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

const PlannerMiniMonth: React.FC<{
  selectedDayKey: string;
  scheduledCountsByDay: Record<string, number>;
  clientPreferenceCountsByDay: Record<string, number>;
  workloadSummaryByDay: Record<string, ServicePlannerDayWorkloadSummary>;
  onSelectDay: (dayKey: string) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  dropTarget: PlannerDropTargetConfig;
  ariaLabel?: string;
}> = ({
  selectedDayKey,
  scheduledCountsByDay,
  clientPreferenceCountsByDay,
  workloadSummaryByDay,
  onSelectDay,
  onPreviousMonth,
  onNextMonth,
  dropTarget,
  ariaLabel = 'Mały miesiąc',
}) => {
  const monthGridDayKeys = getServicePlannerMonthGridDayKeys(selectedDayKey);
  const selectedMonthKey = getMonthKeyFromDayKey(selectedDayKey);
  const todayKey = getServicePlannerDayKey(new Date());
  const weekdayLabels = ['pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd'];

  return (
    <section
      aria-label={ariaLabel}
      data-planner-surface="section"
      className="min-w-0 rounded-lg bg-primary-dark-200/16 p-3"
    >
      <div className="flex min-w-0 items-center justify-between gap-2">
        <button
          type="button"
          aria-label="Poprzedni miesiąc"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent bg-primary-dark-200/42 text-primary-light-100 hover:bg-primary-dark-300/48 focus-outline"
          onClick={onPreviousMonth}
        >
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
        </button>
        <h2 className="truncate text-sm font-semibold text-primary-light-100">
          {formatPlannerMonthYearLabel(selectedDayKey)}
        </h2>
        <button
          type="button"
          aria-label="Następny miesiąc"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent bg-primary-dark-200/42 text-primary-light-100 hover:bg-primary-dark-300/48 focus-outline"
          onClick={onNextMonth}
        >
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-normal text-primary-light-400">
        {weekdayLabels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {monthGridDayKeys.map((dayKey) => {
          const scheduledCount = scheduledCountsByDay[dayKey] || 0;
          const preferenceCount = clientPreferenceCountsByDay[dayKey] || 0;
          const loadLevel = workloadSummaryByDay[dayKey]?.loadLevel || 'empty';
          const isHeavyDay = loadLevel === 'heavy';
          const hasWork = scheduledCount > 0;
          const isSelected = dayKey === selectedDayKey;
          const isToday = dayKey === todayKey;
          const isCurrentMonth = getMonthKeyFromDayKey(dayKey) === selectedMonthKey;
          const dayNumber = dateFromServicePlannerDayKey(dayKey).getUTCDate();
          const isDropActive = dropTarget.isDayEnabled && dropTarget.activeDayKey === dayKey;
          const workloadSurfaceClass = isHeavyDay
            ? 'border-transparent bg-status-warning-500/10 text-primary-light-100 hover:border-status-warning-500/20 hover:bg-status-warning-500/14'
            : hasWork
              ? 'border-transparent bg-ratuj-300/10 text-primary-light-100 hover:border-ratuj-300/22 hover:bg-ratuj-300/14'
              : isToday
                ? 'border-primary-light-300/35 bg-primary-dark-100/34 text-primary-light-100'
                : 'border-transparent bg-transparent text-primary-light-300 hover:border-primary-dark-300/35 hover:bg-primary-dark-100/24';

          return (
            <button
              key={dayKey}
              type="button"
              data-testid={`service-planner-month-day-${dayKey}`}
              data-load-level={loadLevel}
              data-planner-drop-target={dropTarget.isDayEnabled ? 'day' : undefined}
              data-drop-active={isDropActive ? 'true' : undefined}
              aria-label={`${formatPlannerShortDayLabel(dayKey)}${
                scheduledCount ? `, zaplanowane: ${scheduledCount}` : ''
              }${isHeavyDay ? ', dużo pracy' : ''}${preferenceCount ? ', preferencja klienta' : ''}`}
              className={`relative flex h-9 min-w-0 items-center justify-center rounded-md border text-xs font-semibold tabular-nums transition-colors focus-outline ${
                isDropActive
                  ? 'border-ratuj-300/40 bg-ratuj-300/12 text-primary-light-100 ring-1 ring-ratuj-300/10'
                  : workloadSurfaceClass
              } ${isSelected ? 'ring-1 ring-ratuj-300/35' : ''} ${
                isCurrentMonth ? '' : 'opacity-45'
              }`}
              onClick={() => onSelectDay(dayKey)}
              onDragEnter={
                dropTarget.isDayEnabled
                  ? (event) => dropTarget.onDragOver(event, dayKey)
                  : undefined
              }
              onDragOver={
                dropTarget.isDayEnabled
                  ? (event) => dropTarget.onDragOver(event, dayKey)
                  : undefined
              }
              onDragLeave={
                dropTarget.isDayEnabled
                  ? (event) => dropTarget.onDragLeave(event, dayKey)
                  : undefined
              }
              onDrop={
                dropTarget.isDayEnabled ? (event) => dropTarget.onDrop(event, dayKey) : undefined
              }
            >
              <span>{dayNumber}</span>
              {scheduledCount > 0 ? (
                <span className="absolute right-0.5 top-0.5 rounded bg-ratuj-300/22 px-1 text-[9px] leading-3 text-ratuj-200">
                  {scheduledCount}
                </span>
              ) : null}
              {preferenceCount > 0 ? (
                <span
                  className="absolute bottom-0.5 h-1 w-1 rounded-full bg-primary-light-400"
                  aria-hidden="true"
                />
              ) : null}
            </button>
          );
        })}
      </div>
    </section>
  );
};

const PlannerPanel: React.FC<{
  title: string;
  subtitle?: string;
  items: ServicePlannerItem[];
  emptyTitle: string;
  emptyMessage: string;
  dateMode: PlannerItemDateMode;
  actionScope: 'unscheduled' | 'scheduled' | 'none';
  activeAction: ActivePlannerAction | null;
  clearState: ClearPlannerState | null;
  onOpenPlannerForm: (item: ServicePlannerItem, mode: PlannerActionMode) => void;
  onRequestClearConfirm: (requestId: string) => void;
  onCancelClear: () => void;
  onConfirmClear: (requestId: string) => void;
  renderPlanningForm: (
    item?: ServicePlannerItem,
    anchorViewport?: PlannerDecisionViewport
  ) => React.ReactNode;
  dragSource: PlannerDragSourceConfig;
  dropTarget?: PlannerDropTargetConfig;
  dropDayKey?: string;
  anchorViewport?: PlannerDecisionViewport;
  showInlineForm?: boolean;
  compact?: boolean;
  quiet?: boolean;
}> = ({
  title,
  subtitle,
  items,
  emptyTitle,
  emptyMessage,
  dateMode,
  actionScope,
  activeAction,
  clearState,
  onOpenPlannerForm,
  onRequestClearConfirm,
  onCancelClear,
  onConfirmClear,
  renderPlanningForm,
  dragSource,
  dropTarget,
  dropDayKey,
  anchorViewport = 'desktop',
  showInlineForm = false,
  compact = false,
  quiet = false,
}) => {
  const canDropOnEmpty = Boolean(dropTarget?.isDayEnabled && dropDayKey);
  const isEmptyDropActive = Boolean(
    canDropOnEmpty && dropTarget?.activeDayKey && dropTarget.activeDayKey === dropDayKey
  );

  return (
    <section
      aria-label={title}
      data-planner-surface={quiet ? 'section-muted' : 'section'}
      className={quiet ? PLANNER_PANEL_QUIET_SURFACE_CLASS : PLANNER_PANEL_SURFACE_CLASS}
    >
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-primary-light-100 sm:text-base">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-0.5 truncate text-xs text-primary-light-400">{subtitle}</p>
          ) : null}
        </div>
        <Badge variant="neutral" size="sm" className="shrink-0 tabular-nums">
          {items.length}
        </Badge>
      </div>

      {items.length === 0 ? (
        <div
          data-testid={dropDayKey ? `service-planner-day-drop-${dropDayKey}` : undefined}
          data-planner-drop-target={canDropOnEmpty ? 'day' : undefined}
          data-drop-active={isEmptyDropActive ? 'true' : undefined}
          className={`mt-3 rounded-lg border-l-2 px-3 py-4 transition-colors ${
            isEmptyDropActive
              ? 'border-ratuj-300/40 border-l-ratuj-300/75 bg-ratuj-300/12 ring-1 ring-ratuj-300/10'
              : 'border-transparent border-l-primary-dark-300/45 bg-primary-dark-100/24'
          }`}
          onDragEnter={
            canDropOnEmpty && dropTarget && dropDayKey
              ? (event) => dropTarget.onDragOver(event, dropDayKey)
              : undefined
          }
          onDragOver={
            canDropOnEmpty && dropTarget && dropDayKey
              ? (event) => dropTarget.onDragOver(event, dropDayKey)
              : undefined
          }
          onDragLeave={
            canDropOnEmpty && dropTarget && dropDayKey
              ? (event) => dropTarget.onDragLeave(event, dropDayKey)
              : undefined
          }
          onDrop={
            canDropOnEmpty && dropTarget && dropDayKey
              ? (event) => dropTarget.onDrop(event, dropDayKey)
              : undefined
          }
        >
          <p className="text-sm font-medium text-primary-light-100">{emptyTitle}</p>
          {emptyMessage ? (
            <p className="mt-1 text-sm leading-relaxed text-primary-light-300">{emptyMessage}</p>
          ) : null}
        </div>
      ) : (
        <div className={`${compact ? 'mt-3 grid gap-2 lg:grid-cols-2' : 'mt-3 space-y-2.5'}`}>
          {items.map((item) => (
            <PlannerItemRow
              key={`${title}-${item.requestId}`}
              item={item}
              dateMode={dateMode}
              actionScope={actionScope}
              activeAction={activeAction}
              clearState={clearState}
              onOpenPlannerForm={onOpenPlannerForm}
              onRequestClearConfirm={onRequestClearConfirm}
              onCancelClear={onCancelClear}
              onConfirmClear={onConfirmClear}
              renderPlanningForm={renderPlanningForm}
              dragSource={dragSource}
              anchorViewport={anchorViewport}
              showInlineForm={showInlineForm}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const PlannerItemRow: React.FC<{
  item: ServicePlannerItem;
  dateMode: PlannerItemDateMode;
  actionScope: 'unscheduled' | 'scheduled' | 'none';
  activeAction: ActivePlannerAction | null;
  clearState: ClearPlannerState | null;
  onOpenPlannerForm: (item: ServicePlannerItem, mode: PlannerActionMode) => void;
  onRequestClearConfirm: (requestId: string) => void;
  onCancelClear: () => void;
  onConfirmClear: (requestId: string) => void;
  renderPlanningForm: (
    item?: ServicePlannerItem,
    anchorViewport?: PlannerDecisionViewport
  ) => React.ReactNode;
  dragSource: PlannerDragSourceConfig;
  anchorViewport: PlannerDecisionViewport;
  showInlineForm: boolean;
  collapsibleActions?: boolean;
  isActionExpanded?: boolean;
  onToggleActions?: () => void;
}> = ({
  item,
  dateMode,
  actionScope,
  activeAction,
  clearState,
  onOpenPlannerForm,
  onRequestClearConfirm,
  onCancelClear,
  onConfirmClear,
  renderPlanningForm,
  dragSource,
  anchorViewport,
  showInlineForm,
  collapsibleActions = false,
  isActionExpanded = false,
  onToggleActions,
}) => {
  const request = item.request;
  const subjectLabel = getRepairRequestSubjectLabel(request);
  const operationalIndex = getRepairRequestOperationalIndexLabel(request);
  const scheduleLabel =
    dateMode === 'scheduleTime'
      ? formatPlannerTime(item.scheduledStartAt)
      : dateMode === 'schedule'
        ? formatPlannerDateTime(item.scheduledStartAt)
        : formatPlannerDate(item.clientPreferenceDate);
  const scheduleEndLabel =
    dateMode === 'scheduleTime'
      ? formatPlannerTime(item.scheduledEndAt)
      : dateMode === 'schedule'
        ? formatPlannerDateTime(item.scheduledEndAt)
        : null;
  const statusLabel = getRepairRequestStatusLabel(request.status);
  const readableParts = getPlannerReadableEventParts(item);
  const usesReadableScheduledContract =
    actionScope === 'scheduled' || dateMode === 'scheduleTime' || Boolean(item.scheduledStartAt);
  const canDrag =
    dragSource.isEnabled &&
    !item.scheduledStartAt &&
    (actionScope === 'unscheduled' ||
      (actionScope === 'none' && Boolean(item.clientPreferenceDate)));
  const isDragging = dragSource.draggingRequestId === item.requestId;
  const isActiveItem = activeAction?.requestId === item.requestId;
  const usesCollapsibleScheduledActions = collapsibleActions && actionScope === 'scheduled';
  const shouldShowActionRow =
    !usesCollapsibleScheduledActions ||
    isActionExpanded ||
    isActiveItem ||
    clearState?.requestId === item.requestId;
  const isCollapsedScheduledActionButton =
    usesCollapsibleScheduledActions && !shouldShowActionRow && Boolean(onToggleActions);
  const handleCollapsedActionKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!isCollapsedScheduledActionButton || !onToggleActions) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggleActions();
    }
  };

  return (
    <article
      data-testid={`service-planner-item-${item.requestId}`}
      data-planner-item-surface="true"
      data-planner-surface="item"
      data-planner-item-state={isActiveItem ? 'active' : isDragging ? 'dragging' : 'idle'}
      data-planner-action-surface-root={usesCollapsibleScheduledActions ? 'true' : undefined}
      data-planner-scheduled-actions={
        usesCollapsibleScheduledActions ? (shouldShowActionRow ? 'open' : 'closed') : undefined
      }
      draggable={canDrag ? true : undefined}
      data-draggable={canDrag ? 'true' : undefined}
      data-dragging={isDragging ? 'true' : undefined}
      role={isCollapsedScheduledActionButton ? 'button' : undefined}
      tabIndex={isCollapsedScheduledActionButton ? 0 : undefined}
      aria-expanded={usesCollapsibleScheduledActions ? shouldShowActionRow : undefined}
      className={`${PLANNER_ITEM_BASE_CLASS} ${
        isDragging
          ? PLANNER_ITEM_DRAG_SURFACE_CLASS
          : isActiveItem
            ? PLANNER_ITEM_ACTIVE_SURFACE_CLASS
            : PLANNER_ITEM_SURFACE_CLASS
      }`}
      onClick={isCollapsedScheduledActionButton ? onToggleActions : undefined}
      onKeyDown={handleCollapsedActionKeyDown}
      onDragStart={canDrag ? (event) => dragSource.onDragStart(event, item) : undefined}
      onDragEnd={canDrag ? dragSource.onDragEnd : undefined}
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          {usesReadableScheduledContract ? (
            <>
              <p className="hidden line-clamp-2 break-words text-sm font-semibold leading-snug text-primary-light-100 sm:block">
                {buildPlannerReadableEventLine(readableParts)}
              </p>
              <p className="truncate text-sm font-semibold leading-snug text-primary-light-100 sm:hidden">
                {buildPlannerReadableEventMobileLead(readableParts)}
              </p>
              <p className="mt-0.5 line-clamp-2 break-words text-xs leading-snug text-primary-light-400 sm:hidden">
                {buildPlannerReadableEventMobileDetail(readableParts)}
              </p>
            </>
          ) : (
            <p className="line-clamp-2 break-words text-sm font-semibold leading-snug text-primary-light-100">
              {subjectLabel}
            </p>
          )}
          <p className="mt-0.5 truncate text-xs text-primary-light-400">{operationalIndex}</p>
        </div>
        {canDrag ? (
          <span
            className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-dark-200/46 text-primary-light-300 md:inline-flex"
            title="Przeciągnij do dnia"
            aria-hidden="true"
          >
            <Bars3Icon className="h-4 w-4" />
          </span>
        ) : null}
      </div>

      {usesReadableScheduledContract ? (
        <div className="mt-3 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-primary-light-400">
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <ClockIcon className="h-4 w-4 shrink-0 text-ratuj-300" aria-hidden="true" />
            <span className="truncate">
              {scheduleLabel
                ? scheduleEndLabel
                  ? `${scheduleLabel} - ${scheduleEndLabel}`
                  : scheduleLabel
                : 'Bez godziny pracy'}
            </span>
          </span>
          <span className="min-w-0 truncate">{statusLabel}</span>
        </div>
      ) : (
        <>
          <div className="mt-3 grid min-w-0 gap-2 text-xs text-primary-light-300 sm:grid-cols-2">
            <span className="inline-flex min-w-0 items-center gap-1.5">
              {dateMode === 'schedule' ? (
                <ClockIcon className="h-4 w-4 shrink-0 text-ratuj-300" aria-hidden="true" />
              ) : (
                <CalendarDaysIcon
                  className="h-4 w-4 shrink-0 text-primary-light-400"
                  aria-hidden="true"
                />
              )}
              <span className="truncate">
                {scheduleLabel
                  ? scheduleEndLabel
                    ? `${scheduleLabel} - ${scheduleEndLabel}`
                    : scheduleLabel
                  : dateMode === 'schedule'
                    ? 'Bez terminu pracy'
                    : 'Brak preferencji'}
              </span>
            </span>
            <span className="min-w-0 truncate text-primary-light-400">
              {getDeviceMeta(request)}
            </span>
          </div>

          <p className="mt-2 truncate text-xs text-primary-light-400">{statusLabel}</p>
        </>
      )}

      {item.hasScheduleConflict ? (
        <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-status-error-300">
          <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
          Kolizja terminu
        </p>
      ) : null}

      {shouldShowActionRow ? (
        <div data-planner-action-row="inline" className={PLANNER_ACTION_ROW_CLASS}>
          {actionScope === 'unscheduled' ? (
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={() => onOpenPlannerForm(item, 'schedule')}
              disabled={
                activeAction?.requestId === item.requestId && activeAction.mode === 'schedule'
              }
            >
              Zaplanuj
            </Button>
          ) : null}
          {actionScope === 'scheduled' ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className={PLANNER_TONAL_BUTTON_CLASS}
                icon={<PencilSquareIcon className="h-4 w-4" aria-hidden="true" />}
                onClick={() => onOpenPlannerForm(item, 'reschedule')}
                disabled={
                  activeAction?.requestId === item.requestId && activeAction.mode === 'reschedule'
                }
              >
                Przełóż
              </Button>
              <Button
                type="button"
                size="sm"
                variant="neutral"
                icon={<TrashIcon className="h-4 w-4" aria-hidden="true" />}
                className={PLANNER_DESTRUCTIVE_TONAL_BUTTON_CLASS}
                onClick={() => onRequestClearConfirm(item.requestId)}
                disabled={clearState?.requestId === item.requestId && clearState.isSubmitting}
              >
                Usuń z planu
              </Button>
            </>
          ) : null}
          <Link to={buildPlannerDetailHref(item.requestId)} className={PLANNER_NAV_LINK_CLASS}>
            <span>Otwórz</span>
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      ) : null}

      {clearState?.requestId === item.requestId ? (
        <ClearPlanConfirm
          requestId={item.requestId}
          anchorViewport={anchorViewport}
          isSubmitting={clearState.isSubmitting}
          error={clearState.error}
          onCancel={onCancelClear}
          onConfirm={() => onConfirmClear(item.requestId)}
        />
      ) : null}

      {showInlineForm && activeAction?.requestId === item.requestId ? (
        <div className="mt-3 lg:hidden">{renderPlanningForm(item, anchorViewport)}</div>
      ) : null}
    </article>
  );
};
const ClearPlanConfirm: React.FC<{
  requestId: string;
  anchorViewport: PlannerDecisionViewport;
  isSubmitting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ requestId, anchorViewport, isSubmitting, error, onCancel, onConfirm }) => (
  <div
    data-planner-decision-anchor="clear"
    data-planner-request-id={requestId}
    data-planner-viewport={anchorViewport}
    className={`mt-3 rounded-lg border-l-2 border-l-status-error-500/65 bg-status-error-500/10 p-3 ${PLANNER_DECISION_SCROLL_MARGIN_CLASS}`}
  >
    <p
      className="text-sm font-semibold text-status-error-300"
      tabIndex={-1}
      data-planner-focus-target="true"
    >
      Usunąć termin z planu?
    </p>
    <p className="mt-1 text-sm leading-relaxed text-primary-light-300">
      Zlecenie wróci do listy do zaplanowania. Nie zmieni to statusu sprawy ani preferencji klienta.
    </p>
    {error ? (
      <p className="mt-2 text-sm font-medium text-status-error-300" role="alert">
        {error}
      </p>
    ) : null}
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="neutral"
        className={PLANNER_DESTRUCTIVE_TONAL_BUTTON_CLASS}
        isLoading={isSubmitting}
        disabled={isSubmitting}
        onClick={onConfirm}
      >
        Usuń z planu
      </Button>
      <Button
        type="button"
        size="sm"
        variant="neutral"
        className={PLANNER_TONAL_BUTTON_CLASS}
        disabled={isSubmitting}
        onClick={onCancel}
      >
        Anuluj
      </Button>
    </div>
  </div>
);

const PlannerScheduleForm: React.FC<{
  mode: PlannerActionMode;
  requestId: string;
  item: ServicePlannerItem | null;
  unavailableMessage: string | null;
  values: PlannerFormValues;
  errors: PlannerFormErrors;
  submitError: string | null;
  isSubmitting: boolean;
  openedFromDrop: boolean;
  schedulePreview: PlannerFormSchedulePreview;
  anchorViewport: PlannerDecisionViewport;
  onChange: (patch: Partial<PlannerFormValues>) => void;
  onCancel: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}> = ({
  mode,
  requestId,
  item,
  unavailableMessage,
  values,
  errors,
  submitError,
  isSubmitting,
  openedFromDrop,
  schedulePreview,
  anchorViewport,
  onChange,
  onCancel,
  onSubmit,
}) => {
  const request = item?.request || null;
  const isPendingDropReschedule =
    openedFromDrop && mode === 'reschedule' && Boolean(values.startTime);
  const pendingDropDayLabel = values.date ? formatPlannerDayLabel(values.date) : null;
  const pendingDropTimeLabel = values.endTime
    ? `${values.startTime} - ${values.endTime}`
    : values.startTime;
  const title = isPendingDropReschedule
    ? `Przełóż na ${values.startTime}`
    : mode === 'reschedule'
      ? 'Przełóż pracę'
      : 'Zaplanuj pracę';
  const submitLabel = mode === 'reschedule' ? 'Zapisz zmianę' : 'Zaplanuj';
  const cancelLabel = isPendingDropReschedule ? 'Cofnij' : 'Anuluj';
  const preferenceLabel = item ? formatPlannerDate(item.clientPreferenceDate) : null;
  const selectedPreferenceDayKey = item?.clientPreferenceDate
    ? getServicePlannerDayKey(item.clientPreferenceDate)
    : null;
  const dropUsesDifferentPreferenceDay =
    openedFromDrop && selectedPreferenceDayKey && values.date !== selectedPreferenceDayKey;
  const pendingDurationLabel =
    item?.scheduledStartAt && item.scheduledEndAt && item.scheduledEndAt > item.scheduledStartAt
      ? formatPlannerDuration(
          Math.round((item.scheduledEndAt.getTime() - item.scheduledStartAt.getTime()) / 60000)
        )
      : null;
  const outsideWorkingHoursCopy =
    schedulePreview.workingHoursSource === 'businessHours'
      ? `Poza godzinami pracy. ${schedulePreview.workingHoursLabel}. To informacja, zapis nie jest blokowany.`
      : `Poza standardowym oknem planera. ${schedulePreview.workingHoursLabel}. Realne godziny pracy serwisu nie są potwierdzone w planerze.`;

  return (
    <section
      data-testid="service-planner-form"
      data-planner-decision-anchor="form"
      data-planner-request-id={requestId}
      data-planner-viewport={anchorViewport}
      data-planner-decision-surface="true"
      data-planner-surface="decision"
      className={`${PLANNER_DECISION_SURFACE_CLASS} ${PLANNER_DECISION_SCROLL_MARGIN_CLASS}`}
      aria-label="Termin pracy serwisu"
    >
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-normal text-ratuj-300">
          Termin pracy serwisu
        </p>
        <h2
          className="mt-1 text-base font-semibold text-primary-light-100"
          tabIndex={-1}
          data-planner-focus-target="true"
        >
          {title}
        </h2>
      </div>

      {unavailableMessage || !item || !request ? (
        <div className="mt-3 space-y-3">
          <Alert variant="warning" title="Nie można zapisać terminu">
            {unavailableMessage || 'Zlecenie nie jest już dostępne w aktywnym planerze.'}
          </Alert>
          <Button
            type="button"
            size="sm"
            variant="neutral"
            className={PLANNER_TONAL_BUTTON_CLASS}
            onClick={onCancel}
          >
            Anuluj
          </Button>
        </div>
      ) : (
        <form className="mt-3 space-y-3" onSubmit={onSubmit}>
          <div className="rounded-lg bg-primary-dark-100/30 p-3">
            <p className="line-clamp-2 break-words text-sm font-semibold leading-snug text-primary-light-100">
              {getRepairRequestSubjectLabel(request)}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-primary-light-300">
              {getRepairRequestOperationalIndexLabel(request)} · {getDeviceMeta(request)} ·{' '}
              {getClientLabel(request)}
            </p>
          </div>

          {openedFromDrop && !isPendingDropReschedule ? (
            <Alert variant="info">
              <span className="block font-semibold text-primary-light-100">
                Ustaw termin pracy serwisu
              </span>
              <span className="mt-0.5 block">
                {values.startTime
                  ? 'Wybrano godzinę z kalendarza. Sprawdź termin i potwierdź.'
                  : 'Wybrano dzień z kalendarza. Wybierz godzinę i potwierdź.'}
              </span>
            </Alert>
          ) : null}

          {isPendingDropReschedule ? (
            <div
              data-testid="service-planner-pending-reschedule"
              className="rounded-lg border-l-2 border-l-ratuj-300/65 bg-ratuj-300/10 px-3 py-3"
            >
              <p className="text-sm font-semibold text-primary-light-100">
                Przełóż na {pendingDropTimeLabel}
              </p>
              {pendingDropDayLabel ? (
                <p className="mt-1 text-sm leading-relaxed text-primary-light-300">
                  {pendingDropDayLabel}. Zapis nastąpi dopiero po potwierdzeniu.
                </p>
              ) : null}
              {item.scheduledStartAt ? (
                <p className="mt-1 text-xs leading-relaxed text-primary-light-400">
                  Poprzedni termin: {formatPlannerDateTime(item.scheduledStartAt)}
                </p>
              ) : null}
              {pendingDurationLabel ? (
                <p className="mt-1 text-xs leading-relaxed text-primary-light-400">
                  Czas pracy: {pendingDurationLabel}
                </p>
              ) : null}
            </div>
          ) : null}

          {item.clientPreferenceDate ? (
            <Alert variant="info" title="Preferencja klienta">
              {dropUsesDifferentPreferenceDay
                ? 'Wybrany dzień pracy nie zmienia preferencji klienta. Preferencja klienta pozostaje tylko sugestią.'
                : 'Preferencja klienta jest tylko sugestią.'}
              {preferenceLabel ? ` Klient wskazał: ${preferenceLabel}.` : ''}
            </Alert>
          ) : null}

          {schedulePreview.hasScheduleConflict ? (
            <p
              data-testid="service-planner-form-conflict-preview"
              role="status"
              className="rounded-lg border-l-2 border-l-status-warning-500/60 bg-status-warning-500/10 px-3 py-2 text-sm font-medium text-status-warning-300"
            >
              Ten termin koliduje z inną zaplanowaną pracą.
            </p>
          ) : null}

          {schedulePreview.isHeavyDay ? (
            <p
              data-testid="service-planner-form-heavy-day-preview"
              className="rounded-lg bg-primary-dark-100/24 px-3 py-2 text-sm text-primary-light-300"
            >
              Ten dzień ma już dużo zaplanowanej pracy.
            </p>
          ) : null}

          {schedulePreview.isOutsideWorkingHours ? (
            <p
              data-testid="service-planner-outside-working-hours"
              role="status"
              className="rounded-lg bg-primary-dark-100/24 px-3 py-2 text-sm text-primary-light-300"
            >
              {outsideWorkingHoursCopy}
            </p>
          ) : null}

          {!isPendingDropReschedule ? (
            <>
              <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                <Input
                  id={`service-plan-date-${item.requestId}`}
                  label="Data"
                  type="date"
                  value={values.date}
                  error={errors.date}
                  disabled={isSubmitting}
                  onChange={(event) => onChange({ date: event.target.value })}
                />
                <Input
                  id={`service-plan-start-${item.requestId}`}
                  label="Godzina startu"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  autoComplete="off"
                  placeholder="HH:mm"
                  value={values.startTime}
                  error={errors.startTime}
                  disabled={isSubmitting}
                  onChange={(event) => onChange({ startTime: event.target.value })}
                />
                <Input
                  id={`service-plan-end-${item.requestId}`}
                  label="Godzina końca"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  autoComplete="off"
                  placeholder="HH:mm"
                  value={values.endTime}
                  error={errors.endTime}
                  disabled={isSubmitting}
                  onChange={(event) => onChange({ endTime: event.target.value })}
                />
              </div>

              <div>
                <label
                  htmlFor={`service-plan-note-${item.requestId}`}
                  className="mb-1 block text-sm font-medium text-primary-light-200"
                >
                  Notatka
                </label>
                <textarea
                  id={`service-plan-note-${item.requestId}`}
                  value={values.note}
                  maxLength={SERVICE_SCHEDULE_NOTE_MAX_LENGTH}
                  disabled={isSubmitting}
                  rows={3}
                  className={`w-full min-w-0 resize-y rounded-lg border bg-primary-dark-100/50 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ratuj-300/70 disabled:cursor-not-allowed disabled:opacity-50 ${
                    errors.note ? 'border-status-error-500' : 'border-primary-dark-300/32'
                  }`}
                  placeholder="Opcjonalna notatka dla serwisu"
                  onChange={(event) => onChange({ note: event.target.value })}
                />
                <div className="mt-1 flex items-center justify-between gap-3 text-xs">
                  {errors.note ? (
                    <span className="text-status-error-300">{errors.note}</span>
                  ) : (
                    <span className="text-primary-light-400">To nie zmieni statusu sprawy.</span>
                  )}
                  <span className="shrink-0 text-primary-light-400">
                    {values.note.trim().length}/{SERVICE_SCHEDULE_NOTE_MAX_LENGTH}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-lg bg-primary-dark-100/20 px-3 py-2 text-xs leading-relaxed text-primary-light-400">
              Notatka pozostaje bez zmian; data i godziny pochodzą z przeciągniętego slotu.
            </div>
          )}

          {submitError ? (
            <Alert variant="error" title="Nie zapisano terminu">
              {submitError}
            </Alert>
          ) : null}

          <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center">
            <Button
              type="submit"
              variant="primary"
              size="md"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              className="sm:w-auto"
            >
              {submitLabel}
            </Button>
            <Button
              type="button"
              variant="neutral"
              size="md"
              className={PLANNER_TONAL_BUTTON_CLASS}
              disabled={isSubmitting}
              onClick={onCancel}
            >
              {cancelLabel}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
};

export default ServicePlannerPage;
